import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Pipe, NgModule, Directive, Input, HostBinding, HostListener, Component, ChangeDetectionStrategy, EventEmitter, Output, ContentChildren, ViewEncapsulation, ViewChild } from '@angular/core';
import * as i1 from '@play/translate';
import { TranslationModule } from '@play/translate';
import * as momentTz from 'moment-timezone';
import * as momentImported from 'moment';
import * as i1$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { trigger, state, style, transition, animate } from '@angular/animations';
import * as i2 from '@angular/platform-browser';
import { Map } from 'immutable';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as i2$1 from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { filter, isUndefined, find, map, intersection } from 'lodash';
import { intlSort } from '@play/utility';

const BASE_CONFIG = new InjectionToken("common.config");
const commonBaseConfig = {
    timezone: "Europe/Berlin",
    navnode: "/play",
};

const moment$1 = momentTz;
class FormatDate {
    constructor(translate, config) {
        this.translate = translate;
        this.config = config;
    }
    getWeekDay(diffDays, date) {
        if (diffDays >= 0 && diffDays < 1) {
            return this.translate.translate("common:date-today", {});
        }
        else if (diffDays >= 1 && diffDays < 2) {
            return this.translate.translate("common:date-tomorrow", {});
        }
        return new Intl.DateTimeFormat(Translation.language, { weekday: "long" }).format(date);
    }
    formatDateTime(dateTime, optional) {
        const optionalArguments = Object.assign({ showTime: true, showWeekDay: true, showTimezone: true, timezone: this.config.timezone }, optional);
        const date = moment$1(dateTime, "YYYY-MM-DDTHH:mm:ssZ");
        const dateMoment = moment$1.tz(date, optionalArguments.timezone);
        const startOfDay = moment$1.tz(optionalArguments.timezone).startOf("day");
        const diff = dateMoment.diff(startOfDay, "days", true);
        const options = {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour12: false,
            hour: optionalArguments.showTime ? "2-digit" : undefined,
            minute: optionalArguments.showTime ? "2-digit" : undefined,
            timeZone: optionalArguments.timezone,
            timeZoneName: optionalArguments.showTimezone ? "short" : undefined,
        };
        try {
            const intlWeekDay = this.getWeekDay(diff, date.toDate());
            const formatter = new Intl.DateTimeFormat(Translation.language, options);
            const intlDate = formatter
                .formatToParts(date.toDate())
                .map(({ type, value }) => {
                switch (type) {
                    case "hour":
                        return value === "24" ? "00" : value;
                    default:
                        return value;
                }
            })
                .reduce((string, part) => string + part);
            const returnString = (optionalArguments.showWeekDay ? `${intlWeekDay}, ` : "") + intlDate;
            return returnString;
        }
        catch (error) {
            /**
             * Fallback if Intl is not supported.
             */
            let weekday = "dddd";
            if (diff >= 0 && diff < 1) {
                weekday = `[${this.translate.translate("common:date-today", {})}]`;
            }
            else if (diff >= 1 && diff < 2) {
                weekday = `[${this.translate.translate("common:date-tomorrow", {})}]`;
            }
            const customFormat = (optionalArguments.showWeekDay ? `${weekday}, ` : "") + "DD MMM YYYY HH:mm" + (optionalArguments.showTimezone ? " z" : "");
            return dateMoment.format(customFormat);
        }
    }
    formatDate(dateTime, timezone = this.config.timezone) {
        return this.formatDateTime(dateTime, { showTime: false, showWeekDay: false, showTimezone: false, timezone });
    }
    getFullDateTimeTz(dateTime, timezone = this.config.timezone) {
        return `${this.formatDateTime(dateTime, { timezone })} (${timezone})`;
    }
}
FormatDate.ɵfac = function FormatDate_Factory(t) { return new (t || FormatDate)(i0.ɵɵinject(i1.TranslateService), i0.ɵɵinject(BASE_CONFIG)); };
FormatDate.ɵprov = i0.ɵɵdefineInjectable({ token: FormatDate, factory: FormatDate.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FormatDate, [{
        type: Injectable
    }], function () { return [{ type: i1.TranslateService }, { type: undefined, decorators: [{
                type: Inject,
                args: [BASE_CONFIG]
            }] }]; }, null); })();

class FormatDatePipe {
    constructor(formatter) {
        this.formatter = formatter;
    }
    transform(value, onlyDate = false) {
        return onlyDate ? this.formatter.formatDate(value) : this.formatter.formatDateTime(value);
    }
}
FormatDatePipe.ɵfac = function FormatDatePipe_Factory(t) { return new (t || FormatDatePipe)(i0.ɵɵdirectiveInject(FormatDate)); };
FormatDatePipe.ɵpipe = i0.ɵɵdefinePipe({ name: "date", type: FormatDatePipe, pure: true });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FormatDatePipe, [{
        type: Pipe,
        args: [{ name: "date" }]
    }], function () { return [{ type: FormatDate }]; }, null); })();

const moment = momentImported;
class FormatTime {
    constructor(translate) {
        this.translate = translate;
    }
    formatForHumans(seconds, includeMinutes = true) {
        const d = moment.duration(seconds, "seconds");
        const daysAmount = d.days();
        const hoursAmount = d.hours();
        const minutesAmount = d.minutes();
        const secondsAmount = d.seconds();
        let text = "";
        if (daysAmount >= 1) {
            text += this.translate.translate("common:n-days", {
                days: daysAmount,
            });
        }
        if (hoursAmount >= 1) {
            if (text.length > 0) {
                text += " ";
            }
            text += this.translate.translate("common:n-hours", {
                hours: hoursAmount,
            });
        }
        if (minutesAmount >= 1 && includeMinutes) {
            if (text.length > 0) {
                text += " ";
            }
            text += this.translate.translate("common:n-minutes", {
                minutes: minutesAmount,
            });
        }
        if (text.length === 0) {
            text = this.translate.translate("common:n-seconds", {
                seconds: secondsAmount,
            });
        }
        return text;
    }
    shortTime(date) {
        return moment(date).calendar(null, {
            sameDay: "[Today, ] HH:mm",
            nextDay: "[Tomorrow, ] HH:mm",
            nextWeek: "MMM Do, HH:mm",
            lastDay: "[Yesterday, ] HH:mm",
            lastWeek: "[Last] dddd",
            sameElse: "MMM Do, HH:mm",
        });
    }
}
FormatTime.ɵfac = function FormatTime_Factory(t) { return new (t || FormatTime)(i0.ɵɵinject(i1.TranslateService)); };
FormatTime.ɵprov = i0.ɵɵdefineInjectable({ token: FormatTime, factory: FormatTime.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FormatTime, [{
        type: Injectable
    }], function () { return [{ type: i1.TranslateService }]; }, null); })();

class FormatTimePipe {
    constructor(formatter) {
        this.formatter = formatter;
    }
    transform(value, ...args) {
        throw new Error("Method not implemented.");
    }
}
FormatTimePipe.ɵfac = function FormatTimePipe_Factory(t) { return new (t || FormatTimePipe)(i0.ɵɵdirectiveInject(FormatTime)); };
FormatTimePipe.ɵpipe = i0.ɵɵdefinePipe({ name: "time", type: FormatTimePipe, pure: true });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FormatTimePipe, [{
        type: Pipe,
        args: [{ name: "time" }]
    }], function () { return [{ type: FormatTime }]; }, null); })();

class CommonsDateModule {
}
CommonsDateModule.ɵfac = function CommonsDateModule_Factory(t) { return new (t || CommonsDateModule)(); };
CommonsDateModule.ɵmod = i0.ɵɵdefineNgModule({ type: CommonsDateModule });
CommonsDateModule.ɵinj = i0.ɵɵdefineInjector({ providers: [FormatDate, FormatTime], imports: [[]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(CommonsDateModule, { declarations: [FormatDatePipe, FormatTimePipe], exports: [FormatDatePipe, FormatTimePipe] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CommonsDateModule, [{
        type: NgModule,
        args: [{
                imports: [],
                declarations: [FormatDatePipe, FormatTimePipe],
                providers: [FormatDate, FormatTime],
                exports: [FormatDatePipe, FormatTimePipe],
            }]
    }], null, null); })();

class Location {
    go(link, newTab) {
        if (newTab && typeof link === "string") {
            window.open(link, "_blank");
        }
        else if (typeof link === "string") {
            document.location.href = link;
        }
        else {
            link();
        }
    }
}
Location.ɵfac = function Location_Factory(t) { return new (t || Location)(); };
Location.ɵprov = i0.ɵɵdefineInjectable({ token: Location, factory: Location.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(Location, [{
        type: Injectable
    }], null, null); })();

class LinkService {
    /**
     * Returns the current hostname
     */
    get location() {
        return window.location.hostname;
    }
    /**
     * Returns current href
     */
    href() {
        return window.location.href;
    }
    /**
     * Links to the login page
     */
    login() {
        return "/user/oauth2/login";
    }
    /**
     * Links to the games page
     */
    games() {
        return "/games";
    }
    /**
     * Returns a link to a match
     *
     * @param leagueUrl
     * @param matchId
     */
    match(leagueUrl, matchId) {
        return `${leagueUrl}match/${matchId}`;
    }
    /**
     * Returns a link to the esl id account page
     */
    manageEslAccount() {
        return "https://account.eslgaming.com/";
    }
    /**
     * Returns a link to the match setup
     *
     * @param leagueUrl
     * @param matchId
     */
    matchSetup(leagueUrl, matchId) {
        return `${leagueUrl}matchsetup/${matchId}`;
    }
    /**
     * Returns a link to the match move page
     *
     * @param leagueUrl
     * @param matchId
     */
    matchDelete(leagueUrl, matchId) {
        return `${leagueUrl}deletematch/${matchId}`;
    }
    /**
     * Returns a link to the match move cancel page
     *
     * @param leagueUrl
     * @param matchId
     */
    matchDeleteCancel(leagueUrl, matchId) {
        return `${leagueUrl}deletematch/abort/${matchId}`;
    }
    /**
     * Returns a link to the match move page
     *
     * @param leagueUrl
     * @param matchId
     */
    matchMove(leagueUrl, matchId) {
        return `${leagueUrl}movematch/ask/${matchId}`;
    }
    /**
     * Returns a link to the match move cancel page
     *
     * @param leagueUrl
     * @param matchId
     */
    matchMoveCancel(leagueUrl, matchId) {
        return `${leagueUrl}movematch/abort/${matchId}`;
    }
    /**
     * Returns a link to the match protest page
     *
     * @param leagueUrl
     * @param matchId
     */
    matchProtest(leagueUrl, matchId) {
        return `${leagueUrl}match/protest/${matchId}`;
    }
    /**
     * Returns a link to a league
     *
     * @param leagueId
     * @param leagueUrl
     */
    league(leagueId, leagueUrl) {
        return `${leagueUrl}`;
    }
    /**
     * Returns a link to a player
     *
     * @param id
     * @param alias
     */
    player(id, alias) {
        return `/player/${id || alias}`;
    }
    /**
     * Returns a link to a team
     *
     * @param id
     */
    team(id) {
        return `/team/${id}`;
    }
    /**
     * Returns a link to a teams contestant.
     * If league is a roster league it will return a link to a roster, if not then it will link to the team page
     *
     * @param leagueUrl
     * @param rosterEnabled
     * @param id
     */
    teamContestant(leagueUrl, rosterEnabled = false, id) {
        if (rosterEnabled) {
            return this.roster(leagueUrl, id);
        }
        return this.team(id);
    }
    /**
     * Returns a link to upload logo of a team
     *
     * @param id
     */
    teamUploadLogo(id) {
        return `/photo_upload/teams/${id}`;
    }
    /**
     * Returns a link to team command center
     *
     * @param id
     */
    teamAdmin(id) {
        return `/team/admin/${id}`;
    }
    /**
     * Returns a link to team edit
     *
     * @param id
     */
    teamEdit(id) {
        return `/team/edit/${id}`;
    }
    /**
     * Returns a link to team premium status
     *
     * @param id
     */
    teamPremiumStatus(id) {
        return `/team_permissions/${id}`;
    }
    /**
     * Returns a link to team trusted status
     *
     * @param id
     */
    teamTrustedStatus(id) {
        return `/trustedstatus/teamstatus?teamId=${id}`;
    }
    /**
     * Returns a link to a roster
     *
     * @param leagueUrl
     * @param teamId
     */
    roster(leagueUrl, teamId) {
        return `${leagueUrl}roster/${teamId}`;
    }
    /**
     * Returns a join team link
     *
     * @param teamId
     * @param teamPassword
     */
    joinTeamLink(teamId, teamPassword) {
        return `https://${this.location}/join_team/?teamid=${teamId}&joinpw=${teamPassword}`;
    }
    /**
     * Returns a link to a player's closed matches in a specific tournament
     *
     * @param id
     * @param alias
     * @param leagueId
     */
    playerClosedMatches(id, alias, leagueId) {
        return `/player/matches/${alias || id}/${leagueId}`;
    }
    /**
     * Returns a link to a team's closed matches in a specific tournament
     *
     * @param id
     * @param alias
     * @param leagueId
     */
    teamClosedMatches(id, alias, leagueId) {
        return `/team/matches/${alias || id}/${leagueId}`;
    }
    /**
     * Returns a link to change the users email
     *
     * @param userId
     */
    changeEmail(userId) {
        return "https://account.eslgaming.com/change-email";
    }
    /**
     * Returns a link to change a users language
     */
    changeLanguage() {
        return "/change_lang/";
    }
    /**
     * Returns a link to change a users timezone
     */
    changeTimezone() {
        return "/timezone/";
    }
    /**
     * Returns a link to a users support tickets
     *
     * @param userId
     */
    mySupports(userId) {
        return `/support/contestant/${userId}/`;
    }
    /**
     * Returns a link for a user to open a support ticket
     */
    openSupport() {
        return "/support/";
    }
    /**
     * Returns a link to a users protest tickets
     *
     * @param userId
     */
    myProtests(userId) {
        return `/protest/contestant/${userId}/`;
    }
    /**
     * Returns a link for a user to open a match protest ticket
     */
    openProtest() {
        return "/protest/add/";
    }
    /**
     * Returns a link for a user to buy premium
     */
    buyPremium() {
        return "/premium/";
    }
    /**
     * Returns a link for a user to manage his premium
     */
    managePremium() {
        return "/account/";
    }
    /**
     * Returns a link for a user to buy trusted pro
     */
    buyTrusted() {
        return "/playercard_portal/";
    }
    /**
     * Returns a link to a ranking
     *
     * @param leagueUrl
     */
    ranking(leagueUrl) {
        return `${leagueUrl}rankings`;
    }
    /**
     * Returns a link to a steam community profile
     *
     * @param steamId
     */
    steamProfileLink(steamId) {
        return `https://steamcommunity.com/profiles/${steamId}`;
    }
}
LinkService.ɵfac = function LinkService_Factory(t) { return new (t || LinkService)(); };
LinkService.ɵprov = i0.ɵɵdefineInjectable({ token: LinkService, factory: LinkService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LinkService, [{
        type: Injectable
    }], null, null); })();

class LocationDirective {
    constructor(location) {
        this.location = location;
    }
    ngOnChanges(changes) {
        this.updateTargetUrlAndHref();
    }
    onClick() {
        if (this.isFunction) {
            this.locationHref();
            return false;
        }
        return true;
    }
    get isLink() {
        return typeof this.locationHref === "string";
    }
    get isFunction() {
        return typeof this.locationHref === "function";
    }
    updateTargetUrlAndHref() {
        const link = this.isLink ? this.locationHref : "";
        this.href = link;
    }
}
LocationDirective.ɵfac = function LocationDirective_Factory(t) { return new (t || LocationDirective)(i0.ɵɵdirectiveInject(Location)); };
LocationDirective.ɵdir = i0.ɵɵdefineDirective({ type: LocationDirective, selectors: [["", "locationHref", ""]], hostVars: 1, hostBindings: function LocationDirective_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("click", function LocationDirective_click_HostBindingHandler() { return ctx.onClick(); });
    } if (rf & 2) {
        i0.ɵɵhostProperty("href", ctx.href, i0.ɵɵsanitizeUrlOrResourceUrl);
    } }, inputs: { locationHref: "locationHref" }, features: [i0.ɵɵNgOnChangesFeature] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LocationDirective, [{
        type: Directive,
        args: [{ selector: "[locationHref]" }]
    }], function () { return [{ type: Location }]; }, { locationHref: [{
            type: Input
        }], href: [{
            type: HostBinding
        }], onClick: [{
            type: HostListener,
            args: ["click"]
        }] }); })();

class CommonsLocationModule {
}
CommonsLocationModule.ɵfac = function CommonsLocationModule_Factory(t) { return new (t || CommonsLocationModule)(); };
CommonsLocationModule.ɵmod = i0.ɵɵdefineNgModule({ type: CommonsLocationModule });
CommonsLocationModule.ɵinj = i0.ɵɵdefineInjector({ providers: [Location, LinkService], imports: [[CommonModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(CommonsLocationModule, { declarations: [LocationDirective], imports: [CommonModule], exports: [LocationDirective] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CommonsLocationModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                declarations: [LocationDirective],
                providers: [Location, LinkService],
                exports: [LocationDirective],
            }]
    }], null, null); })();

const _c0$g = ["*"];
class InlineErrorComponent {
}
InlineErrorComponent.ɵfac = function InlineErrorComponent_Factory(t) { return new (t || InlineErrorComponent)(); };
InlineErrorComponent.ɵcmp = i0.ɵɵdefineComponent({ type: InlineErrorComponent, selectors: [["inline-error"]], ngContentSelectors: _c0$g, decls: 5, vars: 0, consts: [[1, "o-whisper-box", "c-inline-error"], ["xmlns", "http://www.w3.org/2000/svg", "viewBox", "0 0 24 24", 1, "o-whisper-box__icon", "c-inline-error__icon"], ["d", "M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"], [1, "o-whisper-box__description", "c-inline-error__description"]], template: function InlineErrorComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵnamespaceSVG();
        i0.ɵɵelementStart(1, "svg", 1);
        i0.ɵɵelement(2, "path", 2);
        i0.ɵɵelementEnd();
        i0.ɵɵnamespaceHTML();
        i0.ɵɵelementStart(3, "div", 3);
        i0.ɵɵprojection(4);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } }, encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(InlineErrorComponent, [{
        type: Component,
        args: [{
                selector: "inline-error",
                templateUrl: "./inline-error.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, null); })();

const _c0$f = function (a0, a1) { return [a0, a1]; };
class CountryFlagComponent {
    constructor() {
        this.country = "XX";
        this.size = 16;
    }
}
CountryFlagComponent.ɵfac = function CountryFlagComponent_Factory(t) { return new (t || CountryFlagComponent)(); };
CountryFlagComponent.ɵcmp = i0.ɵɵdefineComponent({ type: CountryFlagComponent, selectors: [["country-flag"]], hostVars: 2, hostBindings: function CountryFlagComponent_HostBindings(rf, ctx) { if (rf & 2) {
        i0.ɵɵclassProp("c-country-flag", true);
    } }, inputs: { country: "country", size: "size" }, decls: 1, vars: 4, consts: [[1, "icon", 3, "ngClass"]], template: function CountryFlagComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelement(0, "div", 0);
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(1, _c0$f, "flag-" + ctx.country, "icon-" + ctx.size));
    } }, directives: [i1$1.NgClass], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CountryFlagComponent, [{
        type: Component,
        args: [{
                selector: "country-flag",
                templateUrl: "./flag.component.html",
                host: { "[class.c-country-flag]": "true" },
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { country: [{
            type: Input
        }], size: [{
            type: Input
        }] }); })();

class InheritClassComponent {
    constructor() {
        /**
         * We override the class of the component here, because we use the passed down classes
         * On the component itself and we dont want to have the classes on the parent and the child
         */
        this.removeClassFromComponent = "";
    }
}
InheritClassComponent.ɵfac = function InheritClassComponent_Factory(t) { return new (t || InheritClassComponent)(); };
InheritClassComponent.ɵcmp = i0.ɵɵdefineComponent({ type: InheritClassComponent, selectors: [["ng-component"]], hostVars: 2, hostBindings: function InheritClassComponent_HostBindings(rf, ctx) { if (rf & 2) {
        i0.ɵɵclassMap(ctx.removeClassFromComponent);
    } }, inputs: { classList: ["class", "classList"] }, decls: 0, vars: 0, template: function InheritClassComponent_Template(rf, ctx) { }, encapsulation: 2 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(InheritClassComponent, [{
        type: Component,
        args: [{
                template: "",
            }]
    }], null, { classList: [{
            type: Input,
            args: ["class"]
        }], removeClassFromComponent: [{
            type: HostBinding,
            args: ["class"]
        }] }); })();

class LoadingSpinnerComponent extends InheritClassComponent {
}
LoadingSpinnerComponent.ɵfac = function LoadingSpinnerComponent_Factory(t) { return ɵLoadingSpinnerComponent_BaseFactory(t || LoadingSpinnerComponent); };
LoadingSpinnerComponent.ɵcmp = i0.ɵɵdefineComponent({ type: LoadingSpinnerComponent, selectors: [["loading-spinner"]], features: [i0.ɵɵInheritDefinitionFeature], decls: 3, vars: 1, consts: [[1, "c-block-spinner", 3, "ngClass"], ["viewBox", "25 25 50 50", 1, "c-block-spinner__circular"], ["cx", "50", "cy", "50", "r", "15", "fill", "none", "stroke-width", "5", "stroke-miterlimit", "10", 1, "c-block-spinner__path"]], template: function LoadingSpinnerComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵnamespaceSVG();
        i0.ɵɵelementStart(1, "svg", 1);
        i0.ɵɵelement(2, "circle", 2);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", ctx.classList);
    } }, directives: [i1$1.NgClass], encapsulation: 2, changeDetection: 0 });
const ɵLoadingSpinnerComponent_BaseFactory = /*@__PURE__*/ i0.ɵɵgetInheritedFactory(LoadingSpinnerComponent);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LoadingSpinnerComponent, [{
        type: Component,
        args: [{
                selector: "loading-spinner",
                templateUrl: "./loading-spinner.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, null); })();

function ButtonComponent_loading_spinner_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "loading-spinner", 3);
} }
const _c0$e = function (a0) { return { "c-button__text--loading": a0 }; };
const _c1$5 = ["*"];
class ButtonComponent extends InheritClassComponent {
    constructor() {
        super(...arguments);
        this.type = "contained";
        this.clicked = new EventEmitter();
    }
    ngOnChanges() {
        this.classProperty = {
            [this.classList]: true,
            "c-flat-button": this.type === "flat",
            "c-button": this.type === "contained",
        };
    }
}
ButtonComponent.ɵfac = function ButtonComponent_Factory(t) { return ɵButtonComponent_BaseFactory(t || ButtonComponent); };
ButtonComponent.ɵcmp = i0.ɵɵdefineComponent({ type: ButtonComponent, selectors: [["play-button"]], inputs: { isDisabled: ["disabled", "isDisabled"], isLoading: ["loading", "isLoading"], type: "type" }, outputs: { clicked: "clicked" }, features: [i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature], ngContentSelectors: _c1$5, decls: 4, vars: 6, consts: [[3, "ngClass", "disabled", "click"], [3, "ngClass"], ["class", "c-button__loading-spinner", 4, "ngIf"], [1, "c-button__loading-spinner"]], template: function ButtonComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "button", 0);
        i0.ɵɵlistener("click", function ButtonComponent_Template_button_click_0_listener($event) { return ctx.clicked.emit($event); });
        i0.ɵɵelementStart(1, "span", 1);
        i0.ɵɵprojection(2);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(3, ButtonComponent_loading_spinner_3_Template, 1, 0, "loading-spinner", 2);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", ctx.classProperty)("disabled", ctx.isDisabled || ctx.isLoading);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(4, _c0$e, ctx.isLoading));
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", ctx.isLoading);
    } }, directives: [i1$1.NgClass, i1$1.NgIf, LoadingSpinnerComponent], styles: ["[_nghost-%COMP%], [_nghost-%COMP%]:hover, [_nghost-%COMP%]:focus, [_nghost-%COMP%]:active {\n                background: transparent;\n                border: none;\n                padding: 0;\n                outline: none;\n                box-shadow: none;\n            }"], changeDetection: 0 });
const ɵButtonComponent_BaseFactory = /*@__PURE__*/ i0.ɵɵgetInheritedFactory(ButtonComponent);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ButtonComponent, [{
        type: Component,
        args: [{
                selector: "play-button",
                templateUrl: "./button.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
                styles: [
                    `
            :host,
            :host:hover,
            :host:focus,
            :host:active {
                background: transparent;
                border: none;
                padding: 0;
                outline: none;
                box-shadow: none;
            }
        `,
                ],
            }]
    }], null, { isDisabled: [{
            type: Input,
            args: ["disabled"]
        }], isLoading: [{
            type: Input,
            args: ["loading"]
        }], type: [{
            type: Input
        }], clicked: [{
            type: Output,
            args: ["clicked"]
        }] }); })();

class SquareImageComponent {
    constructor() {
        this.title = "";
        this.alt = "";
    }
}
SquareImageComponent.ɵfac = function SquareImageComponent_Factory(t) { return new (t || SquareImageComponent)(); };
SquareImageComponent.ɵcmp = i0.ɵɵdefineComponent({ type: SquareImageComponent, selectors: [["square-image"]], inputs: { image: "image", title: "title", alt: "alt" }, decls: 2, vars: 3, consts: [[1, "o-ratio", "o-ratio--1:1", "c-media-box"], [1, "c-media-box__media", 3, "src", "alt", "title"]], template: function SquareImageComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelement(1, "img", 1);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("src", ctx.image, i0.ɵɵsanitizeUrl)("alt", ctx.alt)("title", ctx.title);
    } }, styles: ["[_nghost-%COMP%] {\n                display: block;\n                overflow: hidden;\n            }"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SquareImageComponent, [{
        type: Component,
        args: [{
                selector: "square-image",
                templateUrl: "./square-image.component.html",
                styles: [
                    `
            :host {
                display: block;
                overflow: hidden;
            }
        `,
                ],
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { image: [{
            type: Input
        }], title: [{
            type: Input
        }], alt: [{
            type: Input
        }] }); })();

const UP_ARROW = 38;
const DOWN_ARROW = 40;
const RIGHT_ARROW = 39;
const LEFT_ARROW = 37;
const PAGE_UP = 33;
const PAGE_DOWN = 34;
const HOME = 36;
const END = 35;
const ENTER = 13;
const SPACE = 32;
const TAB = 9;
const ESCAPE = 27;
const BACKSPACE = 8;
const DELETE = 46;
const A = 65;
const Z = 90;
const ZERO = 48;
const NINE = 91;

class SvgIconsRepository {
    constructor() {
        this.icons = Map();
    }
    /**
     * Get's the icons svg from the repository
     *
     * @param identifier
     */
    getIcon(identifier) {
        const icon = this.icons.get(identifier);
        return icon ? icon.icon : null;
    }
    /**
     * Sets (adds if not set already) an icon
     *
     * @param icon
     */
    setIcon(icon) {
        this.icons = this.icons.set(icon.identifier, icon);
    }
    /**
     * Adds an icon if not defined yet
     *
     * @param icon
     */
    addIcon(icon) {
        const exists = this.icons.get(icon.identifier);
        if (!exists) {
            this.icons = this.icons.set(icon.identifier, icon);
        }
    }
}
SvgIconsRepository.ɵfac = function SvgIconsRepository_Factory(t) { return new (t || SvgIconsRepository)(); };
SvgIconsRepository.ɵprov = i0.ɵɵdefineInjectable({ token: SvgIconsRepository, factory: SvgIconsRepository.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SvgIconsRepository, [{
        type: Injectable
    }], null, null); })();

class SvgIconComponent {
    constructor(repository, sanitizer) {
        this.repository = repository;
        this.sanitizer = sanitizer;
    }
    get innerHtml() {
        return this.sanitizer.bypassSecurityTrustHtml(this.repository.getIcon(this.icon));
    }
}
SvgIconComponent.ɵfac = function SvgIconComponent_Factory(t) { return new (t || SvgIconComponent)(i0.ɵɵdirectiveInject(SvgIconsRepository), i0.ɵɵdirectiveInject(i2.DomSanitizer)); };
SvgIconComponent.ɵcmp = i0.ɵɵdefineComponent({ type: SvgIconComponent, selectors: [["svg-icon"]], hostVars: 1, hostBindings: function SvgIconComponent_HostBindings(rf, ctx) { if (rf & 2) {
        i0.ɵɵhostProperty("innerHtml", ctx.innerHtml, i0.ɵɵsanitizeHtml);
    } }, inputs: { icon: "icon" }, decls: 0, vars: 0, template: function SvgIconComponent_Template(rf, ctx) { }, styles: ["[_nghost-%COMP%] {\n                display: inline-block;\n            }"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SvgIconComponent, [{
        type: Component,
        args: [{
                selector: "svg-icon",
                template: "",
                changeDetection: ChangeDetectionStrategy.OnPush,
                styles: [
                    `
            :host {
                display: inline-block;
            }
        `,
                ],
            }]
    }], function () { return [{ type: SvgIconsRepository }, { type: i2.DomSanitizer }]; }, { icon: [{
            type: Input
        }], innerHtml: [{
            type: HostBinding,
            args: ["innerHtml"]
        }] }); })();

const _c0$d = function (a0) { return { "c-expansion-panel__chevron--open": a0 }; };
function ExpansionPanelComponent_svg_icon_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "svg-icon", 5);
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵstyleProp("width", 24, "px")("height", 24, "px");
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(6, _c0$d, ctx_r0.expand))("icon", "chevron-right");
} }
const _c1$4 = [[["", "expansion-panel-header", ""]], [["", "expansion-panel-panel", ""]]];
const _c2$1 = function (a0) { return { "c-expansion-panel__content-wrapper--condensed": a0 }; };
const _c3 = ["[expansion-panel-header]", "[expansion-panel-panel]"];
const ANIMATION_TIMING_FUNCTION = "cubic-bezier(0.4, 0.0, 0.2, 1)";
var ExpansionState;
(function (ExpansionState) {
    ExpansionState[ExpansionState["CLOSED"] = 0] = "CLOSED";
    ExpansionState[ExpansionState["OPEN"] = 1] = "OPEN";
})(ExpansionState || (ExpansionState = {}));
class ExpansionPanelComponent {
    constructor() {
        this.expandable = true;
        this.expansionChanged = new EventEmitter();
    }
    click(event) {
        if (this.expandable) {
            this.toggle();
        }
    }
    toggle() {
        this.expand = !this.expand;
        this.expansionChanged.emit(this.expand ? ExpansionState.OPEN : ExpansionState.CLOSED);
    }
    /**
     * @param event
     */
    keyup(event) {
        switch (event.keyCode) {
            case SPACE:
            case ENTER:
                if (this.expandable) {
                    event.preventDefault();
                    this.toggle();
                }
                break;
            default:
                return;
        }
    }
    stopBubbling(event) {
        event.stopPropagation();
    }
}
ExpansionPanelComponent.ɵfac = function ExpansionPanelComponent_Factory(t) { return new (t || ExpansionPanelComponent)(); };
ExpansionPanelComponent.ɵcmp = i0.ɵɵdefineComponent({ type: ExpansionPanelComponent, selectors: [["expansion-panel"]], hostAttrs: ["role", "button"], hostVars: 2, hostBindings: function ExpansionPanelComponent_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("click", function ExpansionPanelComponent_click_HostBindingHandler($event) { return ctx.click($event); })("keyup", function ExpansionPanelComponent_keyup_HostBindingHandler($event) { return ctx.keyup($event); });
    } if (rf & 2) {
        i0.ɵɵattribute("tabindex", 0)("aria-expanded", ctx.expand);
    } }, inputs: { expandable: "expandable", expand: "expand", condensed: "condensed" }, outputs: { expansionChanged: "expansionChanged" }, ngContentSelectors: _c3, decls: 7, vars: 5, consts: [[1, "c-expansion-panel"], [1, "c-expansion-panel__content-wrapper", 3, "ngClass"], [1, "c-expansion-panel__content"], ["class", "c-expansion-panel__chevron", 3, "ngClass", "icon", "width", "height", 4, "ngIf"], [1, "c-expansion-panel__panel", 3, "click", "keyup"], [1, "c-expansion-panel__chevron", 3, "ngClass", "icon"]], template: function ExpansionPanelComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef(_c1$4);
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵelementStart(2, "div", 2);
        i0.ɵɵprojection(3);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(4, ExpansionPanelComponent_svg_icon_4_Template, 1, 8, "svg-icon", 3);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(5, "div", 4);
        i0.ɵɵlistener("click", function ExpansionPanelComponent_Template_div_click_5_listener($event) { return ctx.stopBubbling($event); })("keyup", function ExpansionPanelComponent_Template_div_keyup_5_listener($event) { return ctx.stopBubbling($event); });
        i0.ɵɵprojection(6, 1);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(3, _c2$1, ctx.condensed));
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("ngIf", ctx.expandable);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("@expand", ctx.expand);
    } }, directives: [i1$1.NgClass, i1$1.NgIf, SvgIconComponent], styles: ["[_nghost-%COMP%] {\n                display: block;\n            }"], data: { animation: [
            trigger("expand", [
                state("false", style({ height: 0 })),
                state("true", style({ height: "*" })),
                transition("1 <=> 0", animate(`225ms ${ANIMATION_TIMING_FUNCTION}`)),
            ]),
        ] }, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ExpansionPanelComponent, [{
        type: Component,
        args: [{
                selector: "expansion-panel",
                templateUrl: "./expansion-panel.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
                styles: [
                    `
            :host {
                display: block;
            }
        `,
                ],
                host: {
                    role: "button",
                    "[attr.tabindex]": "0",
                    "[attr.aria-expanded]": "expand",
                    "(click)": "click($event)",
                    "(keyup)": "keyup($event)",
                },
                animations: [
                    trigger("expand", [
                        state("false", style({ height: 0 })),
                        state("true", style({ height: "*" })),
                        transition("1 <=> 0", animate(`225ms ${ANIMATION_TIMING_FUNCTION}`)),
                    ]),
                ],
            }]
    }], null, { expandable: [{
            type: Input
        }], expand: [{
            type: Input
        }], condensed: [{
            type: Input
        }], expansionChanged: [{
            type: Output
        }] }); })();

class PremiumIconComponent {
}
PremiumIconComponent.ɵfac = function PremiumIconComponent_Factory(t) { return new (t || PremiumIconComponent)(); };
PremiumIconComponent.ɵcmp = i0.ɵɵdefineComponent({ type: PremiumIconComponent, selectors: [["premium-icon"]], decls: 2, vars: 0, consts: [["href", "https://play.eslgaming.com/premium", "title", "ESL Premium", 1, "c-premium-icon"], ["src", "https://cdn.eslgaming.com/play/eslstatic/skins/v2008_base/content/16_premium.gif", "title", "ESL Premium", 1, "c-premium-icon__icon"]], template: function PremiumIconComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "a", 0);
        i0.ɵɵelement(1, "img", 1);
        i0.ɵɵelementEnd();
    } }, encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PremiumIconComponent, [{
        type: Component,
        args: [{
                selector: "premium-icon",
                templateUrl: "./premium.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, null); })();

class TrustedIconComponent {
}
TrustedIconComponent.ɵfac = function TrustedIconComponent_Factory(t) { return new (t || TrustedIconComponent)(); };
TrustedIconComponent.ɵcmp = i0.ɵɵdefineComponent({ type: TrustedIconComponent, selectors: [["trusted-icon"]], decls: 2, vars: 0, consts: [["href", "https://play.eslgaming.com/playercard_portal", "title", "ESL Trusted 1/2/3/Pro", 1, "c-trusted-icon"], ["src", "https://cdn.eslgaming.com/play/eslstatic/skins/v2008_base/content/16_trusted.gif", "title", "ESL Trusted 1/2/3/Pro", 1, "c-trusted-icon__icon"]], template: function TrustedIconComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "a", 0);
        i0.ɵɵelement(1, "img", 1);
        i0.ɵɵelementEnd();
    } }, encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TrustedIconComponent, [{
        type: Component,
        args: [{
                selector: "trusted-icon",
                templateUrl: "./trusted.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, null); })();

class CommonsLoadingModule {
}
CommonsLoadingModule.ɵfac = function CommonsLoadingModule_Factory(t) { return new (t || CommonsLoadingModule)(); };
CommonsLoadingModule.ɵmod = i0.ɵɵdefineNgModule({ type: CommonsLoadingModule });
CommonsLoadingModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, TranslationModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(CommonsLoadingModule, { declarations: [LoadingSpinnerComponent], imports: [CommonModule, TranslationModule], exports: [LoadingSpinnerComponent] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CommonsLoadingModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, TranslationModule],
                declarations: [LoadingSpinnerComponent],
                exports: [LoadingSpinnerComponent],
            }]
    }], null, null); })();

const _c0$c = function (a0, a1) { return { "c-labeled-value--align-left": a0, "c-labeled-value--label-top": a1 }; };
const _c1$3 = ["*"];
class LabeledValueComponent extends InheritClassComponent {
}
LabeledValueComponent.ɵfac = function LabeledValueComponent_Factory(t) { return ɵLabeledValueComponent_BaseFactory(t || LabeledValueComponent); };
LabeledValueComponent.ɵcmp = i0.ɵɵdefineComponent({ type: LabeledValueComponent, selectors: [["labeled-value"]], inputs: { label: "label", labelOnTop: "labelOnTop", alignLeft: "alignLeft" }, features: [i0.ɵɵInheritDefinitionFeature], ngContentSelectors: _c1$3, decls: 5, vars: 6, consts: [[1, "c-labeled-value", 3, "ngClass"], [1, "c-labeled-value__value", 3, "ngClass"], [1, "c-labeled-value__label"]], template: function LabeledValueComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵprojection(2);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(3, "div", 2);
        i0.ɵɵtext(4);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(3, _c0$c, ctx.alignLeft, ctx.labelOnTop));
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngClass", ctx.classList);
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate1(" ", ctx.label, " ");
    } }, directives: [i1$1.NgClass], encapsulation: 2, changeDetection: 0 });
const ɵLabeledValueComponent_BaseFactory = /*@__PURE__*/ i0.ɵɵgetInheritedFactory(LabeledValueComponent);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LabeledValueComponent, [{
        type: Component,
        args: [{
                selector: "labeled-value",
                templateUrl: "./labeled-value.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { label: [{
            type: Input
        }], labelOnTop: [{
            type: Input
        }], alignLeft: [{
            type: Input
        }] }); })();

function StepComponent_ng_container_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵprojection(1);
    i0.ɵɵelementContainerEnd();
} }
const _c0$b = ["*"];
class StepComponent {
    constructor(changeDetection) {
        this.changeDetection = changeDetection;
    }
    setActive(active) {
        this.active = active;
        this.changeDetection.markForCheck();
    }
}
StepComponent.ɵfac = function StepComponent_Factory(t) { return new (t || StepComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); };
StepComponent.ɵcmp = i0.ɵɵdefineComponent({ type: StepComponent, selectors: [["step"]], inputs: { stepIndex: "stepIndex", editable: "editable", optional: "optional", title: "title" }, ngContentSelectors: _c0$b, decls: 1, vars: 1, consts: [[4, "ngIf"]], template: function StepComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵtemplate(0, StepComponent_ng_container_0_Template, 2, 0, "ng-container", 0);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.active);
    } }, directives: [i1$1.NgIf], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StepComponent, [{
        type: Component,
        args: [{
                selector: "step",
                templateUrl: "./step.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: i0.ChangeDetectorRef }]; }, { stepIndex: [{
            type: Input
        }], editable: [{
            type: Input
        }], optional: [{
            type: Input
        }], title: [{
            type: Input
        }] }); })();

const _c0$a = function (a0) { return { "c-stepper__step-title--active": a0 }; };
const _c1$2 = function (a0) { return { "c-stepper__step-title__status--active": a0 }; };
class StepTitleComponent {
}
StepTitleComponent.ɵfac = function StepTitleComponent_Factory(t) { return new (t || StepTitleComponent)(); };
StepTitleComponent.ɵcmp = i0.ɵɵdefineComponent({ type: StepTitleComponent, selectors: [["step-title"]], inputs: { index: "index", active: "active", optional: "optional", title: "title" }, decls: 5, vars: 9, consts: [[1, "c-stepper__step-title", 3, "ngClass"], [1, "c-stepper__step-title__status", 3, "ngClass"], [3, "label"]], template: function StepTitleComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵtext(2);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(3, "labeled-value", 2);
        i0.ɵɵtext(4);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(5, _c0$a, ctx.active));
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(7, _c1$2, ctx.active));
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate1(" ", ctx.index, " ");
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("label", ctx.optional ? "Optional" : "");
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate1(" ", ctx.title, " ");
    } }, directives: [i1$1.NgClass, LabeledValueComponent], styles: ["[_nghost-%COMP%] {\n                display: block;\n            }"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StepTitleComponent, [{
        type: Component,
        args: [{
                selector: "step-title",
                templateUrl: "./step-title.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
                styles: [
                    `
            :host {
                display: block;
            }
        `,
                ],
            }]
    }], null, { index: [{
            type: Input
        }], active: [{
            type: Input
        }], optional: [{
            type: Input
        }], title: [{
            type: Input
        }] }); })();

function StepperComponent_step_title_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "step-title", 4);
} if (rf & 2) {
    const step_r1 = ctx.$implicit;
    i0.ɵɵproperty("title", step_r1.title)("index", step_r1.stepIndex + 1)("active", step_r1.active)("optional", step_r1.optional);
} }
const _c0$9 = ["*"];
class StepperComponent {
    constructor(changeDetection) {
        this.changeDetection = changeDetection;
        this.stepped = new EventEmitter();
        this.onDestroy = new Subject();
    }
    ngAfterContentInit() {
        this.steps.forEach((step, index) => {
            step.stepIndex = index;
        });
        this.activateStep(this.step);
        this.steps.changes.pipe(takeUntil(this.onDestroy)).subscribe(x => {
            this.steps.forEach((step, index) => {
                step.stepIndex = index;
            });
            setTimeout(() => this.activateStep(this.step, false), 0);
        });
    }
    ngOnDestroy() {
        this.onDestroy.next();
        this.onDestroy.complete();
    }
    ngOnChanges(changes) {
        this.activateStep(this.step);
    }
    get stepComponents() {
        return this.steps.map(x => x);
    }
    activateStep(stepIndex, notify = true) {
        if (!this.steps) {
            return;
        }
        stepIndex = this.steps.length <= stepIndex ? this.steps.length - 1 : stepIndex;
        this.stepped.emit(stepIndex);
        this.steps.forEach(step => {
            step.setActive(step.stepIndex === stepIndex);
        });
        if (notify) {
            this.steps.notifyOnChanges();
        }
        this.changeDetection.markForCheck();
    }
}
StepperComponent.ɵfac = function StepperComponent_Factory(t) { return new (t || StepperComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); };
StepperComponent.ɵcmp = i0.ɵɵdefineComponent({ type: StepperComponent, selectors: [["stepper"]], contentQueries: function StepperComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
        i0.ɵɵcontentQuery(dirIndex, StepComponent, 0);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.steps = _t);
    } }, inputs: { step: "step" }, outputs: { stepped: "stepped" }, features: [i0.ɵɵNgOnChangesFeature], ngContentSelectors: _c0$9, decls: 5, vars: 1, consts: [[1, "c-stepper"], [1, "c-stepper__steps", "u-divided"], ["class", "c-stepper__steps__step", 3, "title", "index", "active", "optional", 4, "ngFor", "ngForOf"], [1, "c-stepper__step"], [1, "c-stepper__steps__step", 3, "title", "index", "active", "optional"]], template: function StepperComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "article", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵtemplate(2, StepperComponent_step_title_2_Template, 1, 4, "step-title", 2);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(3, "div", 3);
        i0.ɵɵprojection(4);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngForOf", ctx.stepComponents);
    } }, directives: [i1$1.NgForOf, StepTitleComponent], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StepperComponent, [{
        type: Component,
        args: [{
                selector: "stepper",
                templateUrl: "./stepper.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: i0.ChangeDetectorRef }]; }, { step: [{
            type: Input
        }], stepped: [{
            type: Output
        }], steps: [{
            type: ContentChildren,
            args: [StepComponent]
        }] }); })();

class ProgressBarComponent {
}
ProgressBarComponent.ɵfac = function ProgressBarComponent_Factory(t) { return new (t || ProgressBarComponent)(); };
ProgressBarComponent.ɵcmp = i0.ɵɵdefineComponent({ type: ProgressBarComponent, selectors: [["progress-bar"]], inputs: { percent: "percent" }, decls: 2, vars: 2, consts: [[1, "c-progress-bar"], [1, "c-progress-bar__fill"]], template: function ProgressBarComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelement(1, "div", 1);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵstyleProp("width", ctx.percent, "%");
    } }, encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ProgressBarComponent, [{
        type: Component,
        args: [{
                selector: "progress-bar",
                templateUrl: "./progress-bar.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { percent: [{
            type: Input
        }] }); })();

function FileUploadFileComponent_span_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 8);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1("\u2014 ", ctx_r0.error, "");
} }
const _c0$8 = function (a0) { return { "c-file-upload-file--error": a0 }; };
const _c1$1 = function (a0) { return { "u-color-failure": a0 }; };
class FileUploadFileComponent {
}
FileUploadFileComponent.ɵfac = function FileUploadFileComponent_Factory(t) { return new (t || FileUploadFileComponent)(); };
FileUploadFileComponent.ɵcmp = i0.ɵɵdefineComponent({ type: FileUploadFileComponent, selectors: [["file-upload-file"]], inputs: { name: "name", percent: "percent", error: "error" }, decls: 9, vars: 9, consts: [[1, "c-file-upload-file", "o-flag", 3, "ngClass"], [1, "o-flag__figure"], ["width", "36", "viewBox", "0 0 24 24", "xmlns", "http://www.w3.org/2000/svg", 1, "u-svg-text"], ["d", "M6 2c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6H6zm7 7V3.5L18.5 9H13z"], [1, "o-flag__body"], [1, "c-file-upload-file__name", "u-baseline-tiny", 3, "ngClass"], ["class", "c-file-upload-file__error", 4, "ngIf"], [3, "percent"], [1, "c-file-upload-file__error"]], template: function FileUploadFileComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵnamespaceSVG();
        i0.ɵɵelementStart(2, "svg", 2);
        i0.ɵɵelement(3, "path", 3);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵnamespaceHTML();
        i0.ɵɵelementStart(4, "div", 4);
        i0.ɵɵelementStart(5, "div", 5);
        i0.ɵɵtext(6);
        i0.ɵɵtemplate(7, FileUploadFileComponent_span_7_Template, 2, 1, "span", 6);
        i0.ɵɵelementEnd();
        i0.ɵɵelement(8, "progress-bar", 7);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(5, _c0$8, ctx.error));
        i0.ɵɵadvance(5);
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(7, _c1$1, ctx.error));
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate1(" ", ctx.name, " ");
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.error);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("percent", ctx.percent);
    } }, directives: [i1$1.NgClass, i1$1.NgIf, ProgressBarComponent], styles: ["[_nghost-%COMP%] {\n                display: block;\n            }"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FileUploadFileComponent, [{
        type: Component,
        args: [{
                selector: "file-upload-file",
                templateUrl: "./file-upload-file.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
                styles: [
                    `
            :host {
                display: block;
            }
        `,
                ],
            }]
    }], null, { name: [{
            type: Input
        }], percent: [{
            type: Input
        }], error: [{
            type: Input
        }] }); })();

const _c0$7 = function (a0) { return { "c-file-upload-dropzone--highlight": a0 }; };
class FileUploadDropzoneComponent {
    constructor() {
        this.dropped = new EventEmitter();
    }
    onDragOver(event) {
        event.preventDefault();
        this.draggedOver = true;
        // Firefox need Move, Chrome needs copy
        event.dataTransfer.effectAllowed = "copyMove";
    }
    onDragLeave(event) {
        event.preventDefault();
        this.draggedOver = false;
    }
    onDrop(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        this.draggedOver = false;
        const files = evt.dataTransfer.files;
        if (files.length > 0) {
            this.dropped.emit(files);
        }
    }
    onChoose(event) {
        event.preventDefault();
        event.stopPropagation();
        const files = event.target.files;
        if (files.length > 0) {
            this.dropped.emit(files);
        }
    }
}
FileUploadDropzoneComponent.ɵfac = function FileUploadDropzoneComponent_Factory(t) { return new (t || FileUploadDropzoneComponent)(); };
FileUploadDropzoneComponent.ɵcmp = i0.ɵɵdefineComponent({ type: FileUploadDropzoneComponent, selectors: [["file-upload-dropzone"]], hostBindings: function FileUploadDropzoneComponent_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("dragover", function FileUploadDropzoneComponent_dragover_HostBindingHandler($event) { return ctx.onDragOver($event); })("dragenter", function FileUploadDropzoneComponent_dragenter_HostBindingHandler($event) { return ctx.onDragOver($event); })("dragleave", function FileUploadDropzoneComponent_dragleave_HostBindingHandler($event) { return ctx.onDragLeave($event); })("drop", function FileUploadDropzoneComponent_drop_HostBindingHandler($event) { return ctx.onDrop($event); });
    } }, outputs: { dropped: "dropped" }, decls: 9, vars: 9, consts: [[1, "c-file-upload-dropzone", "o-ratio", "o-ratio--16:9", 3, "ngClass"], [1, "c-file-upload-dropzone__wrapper"], [1, "c-file-upload-dropzone__text"], [1, "c-button--primary"], ["type", "file", "multiple", "", 1, "c-file-upload-dropzone__input", "c-file-upload-dropzone__wrapper", 3, "change"]], template: function FileUploadDropzoneComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵelementStart(2, "div", 2);
        i0.ɵɵtext(3);
        i0.ɵɵpipe(4, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(5, "play-button", 3);
        i0.ɵɵtext(6);
        i0.ɵɵpipe(7, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(8, "input", 4);
        i0.ɵɵlistener("change", function FileUploadDropzoneComponent_Template_input_change_8_listener($event) { return ctx.onChoose($event); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(7, _c0$7, ctx.draggedOver));
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(4, 3, "file-upload-dropzone:drop-file-or-choose"), " ");
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(7, 5, "commons:choose"), " ");
    } }, directives: [i1$1.NgClass, ButtonComponent], pipes: [i1.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FileUploadDropzoneComponent, [{
        type: Component,
        args: [{
                selector: "file-upload-dropzone",
                templateUrl: "./file-upload-dropzone.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { dropped: [{
            type: Output
        }], onDragOver: [{
            type: HostListener,
            args: ["dragover", ["$event"]]
        }, {
            type: HostListener,
            args: ["dragenter", ["$event"]]
        }], onDragLeave: [{
            type: HostListener,
            args: ["dragleave", ["$event"]]
        }], onDrop: [{
            type: HostListener,
            args: ["drop", ["$event"]]
        }] }); })();

class SvgIconsModule {
}
SvgIconsModule.ɵfac = function SvgIconsModule_Factory(t) { return new (t || SvgIconsModule)(); };
SvgIconsModule.ɵmod = i0.ɵɵdefineNgModule({ type: SvgIconsModule });
SvgIconsModule.ɵinj = i0.ɵɵdefineInjector({ providers: [SvgIconsRepository], imports: [[CommonModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SvgIconsModule, { imports: [CommonModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SvgIconsModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                providers: [SvgIconsRepository],
            }]
    }], null, null); })();

const DOCUMENT = new InjectionToken("common.document");
function getDocument() {
    return document;
}
class CopyToClipboardDirective {
    constructor(dom) {
        this.dom = dom;
    }
    onClick(event) {
        event.preventDefault();
        let textarea = null;
        try {
            textarea = this.dom.createElement("textarea");
            textarea.style.height = "0";
            textarea.style.left = "-100px";
            textarea.style.opacity = "0";
            textarea.style.position = "fixed";
            textarea.style.top = "-100px";
            textarea.style.width = "0";
            textarea.style["pointer-events"] = "none";
            this.dom.body.appendChild(textarea);
            textarea.value = this.copyToClipboard;
            textarea.select();
            this.dom.execCommand("copy");
        }
        finally {
            if (textarea && textarea.parentNode) {
                textarea.parentNode.removeChild(textarea);
            }
        }
    }
}
CopyToClipboardDirective.ɵfac = function CopyToClipboardDirective_Factory(t) { return new (t || CopyToClipboardDirective)(i0.ɵɵdirectiveInject(DOCUMENT)); };
CopyToClipboardDirective.ɵdir = i0.ɵɵdefineDirective({ type: CopyToClipboardDirective, selectors: [["", "copyToClipboard", ""]], hostBindings: function CopyToClipboardDirective_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("click", function CopyToClipboardDirective_click_HostBindingHandler($event) { return ctx.onClick($event); });
    } }, inputs: { copyToClipboard: "copyToClipboard" } });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CopyToClipboardDirective, [{
        type: Directive,
        args: [{ selector: "[copyToClipboard]" }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [DOCUMENT]
            }] }]; }, { copyToClipboard: [{
            type: Input
        }], onClick: [{
            type: HostListener,
            args: ["click", ["$event"]]
        }] }); })();

/* tslint:disable */
const twitter = `
<svg class="u-svg-text" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.25 1.84726C16.6427 2.11831 15.9911 2.30213 15.3064 2.3842C16.0055 1.96254 16.5406 1.29373 16.7943 0.499242C16.1385 0.889725 15.4147 1.17327 14.6435 1.32697C14.0259 0.663349 13.1474 0.25 12.173 0.25C10.3037 0.25 8.78807 1.77666 8.78807 3.65849C8.78807 3.92538 8.81797 4.18606 8.87572 4.43533C6.06301 4.29303 3.56889 2.93567 1.89961 0.873105C1.60781 1.37577 1.44181 1.96148 1.44181 2.58671C1.44181 3.76961 2.03983 4.81335 2.94717 5.42399C2.39245 5.4053 1.87073 5.2516 1.41399 4.99611V5.03869C1.41399 6.68997 2.58115 8.0681 4.12876 8.38176C3.84521 8.45861 3.54622 8.5012 3.23689 8.5012C3.01832 8.5012 2.80695 8.47939 2.5997 8.43784C3.03067 9.79314 4.28033 10.7787 5.76094 10.8057C4.60305 11.7196 3.14307 12.2628 1.55732 12.2628C1.28409 12.2628 1.01497 12.2462 0.75 12.2161C2.24813 13.185 4.02671 13.75 5.93829 13.75C12.1648 13.75 15.5684 8.55521 15.5684 4.05003L15.557 3.60866C16.222 3.13089 16.7974 2.53063 17.25 1.84726Z" fill="#1076E5"/>
</svg>
`;

/* tslint:disable */
const facebook = `
<svg class="u-svg-text" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.0265 0.75H2.97342C1.74549 0.75 0.75 1.74544 0.75 2.97341V15.0265C0.75 16.2545 1.74545 17.2499 2.97342 17.2499H8.91796L8.92809 11.3537H7.39626C7.19718 11.3537 7.03561 11.1928 7.03484 10.9937L7.0275 9.09312C7.02673 8.89296 7.18879 8.73029 7.38895 8.73029H8.918V6.89385C8.918 4.76267 10.2196 3.60223 12.1208 3.60223H13.6808C13.8804 3.60223 14.0423 3.76404 14.0423 3.96368V5.56626C14.0423 5.76582 13.8805 5.92759 13.681 5.92771L12.7236 5.92816C11.6897 5.92816 11.4895 6.41946 11.4895 7.14047V8.73033H13.7614C13.9779 8.73033 14.1458 8.91935 14.1203 9.13433L13.895 11.0349C13.8735 11.2168 13.7192 11.3538 13.5361 11.3538H11.4997L11.4895 17.25H15.0266C16.2546 17.25 17.25 16.2546 17.25 15.0266V2.97341C17.25 1.74544 16.2545 0.75 15.0265 0.75Z" fill="#1076E5"/>
</svg>
`;

/* tslint:disable */
const chevronRight = `
    <svg class="u-svg-text" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
    </svg>
`;
const chevronLeft = `
    <svg class="u-svg-text" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
    </svg>
`;

/* tslint:disable */
const link = `
<svg class="u-svg-text" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M0 0h24v24H0z" fill="none"/>
    <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
</svg>
`;

/* tslint:disable */
const file = `
    <svg class="u-svg-text" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 2c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6H6zm7 7V3.5L18.5 9H13z"/>
    </svg>
`;

/* tslint:disable */
const info = `
    <svg class="u-svg-text" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path d="M0 0h24v24H0z" fill="none"/>
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
    </svg>
`;

/* tslint:disable */
const group = `
    <svg class="u-svg-text" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
        <path d="M32 22C35.32 22 37.98 19.32 37.98 16C37.98 12.68 35.32 10 32 10C28.68 10 26 12.68 26 16C26 19.32 28.68 22 32 22ZM16 22C19.32 22 21.98 19.32 21.98 16C21.98 12.68 19.32 10 16 10C12.68 10 10 12.68 10 16C10 19.32 12.68 22 16 22ZM16 26C11.34 26 2 28.34 2 33V38H30V33C30 28.34 20.66 26 16 26ZM32 26C31.42 26 30.76 26.04 30.06 26.1C32.38 27.78 34 30.04 34 33V38H46V33C46 28.34 36.66 26 32 26Z" />
    </svg>
`;

/* tslint:disable */
const labelEslMajor = `
<svg class="u-svg-text" xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><g><path d="M196.9 640.1c-1.3-2-3.5-3.2-5.9-3.2H65.9c-2.7 0-5.2 1.6-6.3 4L6.5 755c-2.2-.1-4.4-.2-6.5-.2-2.2 0-4.4.1-6.6.2l-52.9-114c-1.1-2.5-3.6-4.1-6.4-4.1H-191c-2.4 0-4.6 1.2-5.9 3.2-1.3 2-1.5 4.5-.4 6.7l84.7 182.2c-6.6 15.3-9.9 31.6-9.9 48.4 0 67.6 55 122.6 122.6 122.6s122.6-55 122.6-122.6c0-16.8-3.3-33.1-9.9-48.4l84.7-182.2c.8-2.1.7-4.6-.6-6.7zM70.4 650.9h46.3L60.8 770.8l-.4-.2-.1.2c-12.2-6.9-25.5-11.6-39.2-14l49.3-105.9zm-140.8 0L-21.1 757c-13.9 2.4-27.4 7.2-39.6 14.2l-55.9-120.4h46.2zm-33.8 162.2l-.5-1.2-.2.5-75.1-161.5h47.9L-72.6 779c-12.5 9.2-23.2 20.8-31.6 34.1zm166.4 48L29 885.3l12.7 39c1 2.9 1 5.2-.2 6.8-.8 1.2-2.2 1.9-3.8 1.9-1.5 0-3.3-.7-5.1-2L-.6 906.9-33.7 931c-1.9 1.3-3.6 2-5.1 2s-2.9-.7-3.8-1.9c-1.2-1.6-1.2-4-.2-6.9l13.2-38.9-33.1-24.1c-4.1-3-4-5.7-3.6-7.1.5-1.7 2.2-3.6 7-3.6h40.8l12.5-39c1.4-4.7 3.8-5.6 5.5-5.6 1.7 0 4.1 1 5.7 5.6l12.5 39h40.8c3.9 0 6.2 1.2 7 3.5.9 2.2-.2 4.6-3.3 7.1zm42.7-48.6l-.2-.5-.5 1.1c-8.3-13.3-19-24.9-31.6-34.1l59.5-128.1H180l-75.1 161.6z"/><path d="M.8 486C-168.9 486-306 622.8-306 792.3-306 960.9-168.6 1098 0 1098s306-137.4 306-306C306 623.6 168.9 486 .8 486zm-2.5 579.9c-149.4-.3-272.2-123.7-272.2-274.2 0-151 122.8-273.9 274.2-273.9 151 0 273.9 123.1 273.9 274.2-.3 151.6-123.7 274.2-275.9 273.9z"/></g></svg>
`;

/* tslint:disable */
const labelEslOpen = `
<svg class="u-svg-text" xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 485.8c-169.7-.1-307.1 137-307 306.3.1 168.7 137.4 305.9 306.2 305.9 168.7 0 306-137.3 306.1-306 0-168.6-137.2-306.2-305.3-306.2zm-2.6 580.1c-149.5-.1-272.4-123.8-272.4-274.1 0-151.1 123-274 274.2-274 151.1 0 273.9 123.1 273.9 274.4 0 151.4-123.4 273.9-275.7 273.7z"/><path d="M151.3 713c-48.6-79.9-121.9-24.3-121.9-24.3-4.7 3.6-13.5 6.5-19.5 6.6H-9.7c-5.9 0-14.7-2.9-19.5-6.5 0 0-73.3-55.6-121.9 24.3-48.6 79.9-29 166.9-29 166.9 3.4 21.3 14.8 35.5 36.1 33.7 21.3-1.8 67.5-57.4 67.5-57.4 3.8-4.6 11.8-8.3 17.8-8.3H59c5.9 0 13.9 3.7 17.7 8.3 0 0 46.2 55.6 67.6 57.4 21.3 1.8 32.7-12.4 36.1-33.7-.1-.1 19.6-87.1-29.1-167zm-182.1 69.5H-65v33s-7.3 5.5-18.5 5.4c-11.2-.2-16.3-6-16.3-6v-32.3h-32.3s-4-4.1-5.1-15c-1-10.9 4.5-19.7 4.5-19.7h34.2v-34.2s7-3.8 17.2-3.5c10.1.3 17.6 4.1 17.6 4.1l-.1 33.6h32.3s5.6 7.2 6.1 16c.4 8.7-5.4 18.6-5.4 18.6zm95.1 33.8c-12.4 0-22.4-10-22.4-22.4 0-12.4 10-22.4 22.4-22.4 12.3 0 22.4 10 22.4 22.4 0 12.4-10.1 22.4-22.4 22.4zm0-62.9c-12.4 0-22.4-10-22.4-22.4 0-12.4 10-22.4 22.4-22.4 12.3 0 22.4 10 22.4 22.4 0 12.5-10.1 22.4-22.4 22.4zm56.1 38.3c-12.4 0-22.4-10-22.4-22.4 0-12.4 10-22.4 22.4-22.4 12.3 0 22.4 10 22.4 22.4 0 12.4-10.1 22.4-22.4 22.4z"/></svg>
`;

/* tslint:disable */
const labelEslPro = `
<svg class="u-svg-text" xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><g><path fill-rule="evenodd" clip-rule="evenodd" d="M1 486c-169.7-.1-307.1 137-307 306.3.1 168.8 137.4 305.9 306.2 305.9 168.7 0 306-137.3 306.1-306C306.3 623.7 169.1 486.1 1 486zm-2.6 580.2C-151.1 1066-274 942.4-273.9 792c0-151.1 123-274 274.2-274 151.1 0 273.9 123.1 273.9 274.4-.1 151.4-123.5 273.9-275.8 273.8z"/><path d="M53.4 826.6l.3-.4c118.8-16 125.7-131.2 126-152.9.1-6.7-5.9-8.4-17.5-8.4h-58.8l-4.6-4.8c-3.3-14.7-45.9-28.6-98.1-28.6-52.2 0-94.9 15.2-98.1 29.8l-2.3 3.6H-162c-11.6 0-17.6 2.2-17.5 9 .4 21.8 7.3 140.8 128 155.8l.7-.8c11.3 14.7 21.6 22.7 31 29-12.9 4.8-21.6 13.7-21.6 24.3 0 10 7.8 18.6 19.6 23.6l-20 23.1c-25 6.1-41.7 17.3-41.7 30.3 0 19.4 37.7 35.1 84.3 35.1 46.5 0 84.3-15.7 84.3-35.1 0-12.9-16.8-24.2-41.7-30.3l-20-23.2C35.2 900.7 43 892 43 882c0-10.5-8.7-19.7-21.6-24.5 9.2-6.4 20.7-16.1 32-30.9zM99.6 704h42.8c.9 47-34.1 108.1-83 114.5C100 774.8 99.6 704 99.6 704zm-241.7 0h43s-.4 70.8 40.2 114.5c-48.8-6.4-84.1-67.5-83.2-114.5z"/></g></svg>
`;

/* tslint:disable */
const leagueSignup = `
<svg class="u-svg-text" xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><path d="M-102.6 836.5l-188.2-188.1 75.9-76 188.2 188.2M-9.7 777.5l-.5-.4-75.9 75.9v.1l.4.4 106.9 30.9"/><g ><path d="M-161.6 777.5l53.7 53.7V679.3l-53.7-53.6"/><path d="M285.9 711.5v-.2c0-.5-.1-1-.1-1.6 0-.2 0-.3-.1-.5-.1-.5-.2-1.1-.3-1.6v-.1c-.1-.6-.2-1.1-.4-1.7 0-.1-.1-.3-.1-.4-.1-.5-.3-1-.5-1.5v-.1c-.2-.5-.4-1.1-.6-1.6-.1-.1-.1-.3-.2-.4-.2-.5-.4-1-.7-1.4 0-.1-.1-.1-.1-.2l-.9-1.5c-.1-.1-.2-.3-.2-.4-.3-.5-.6-.9-.9-1.4 0 0 0-.1-.1-.1-.3-.5-.7-.9-1.1-1.4l-.3-.3c-.4-.5-.8-.9-1.2-1.3L76.8 492.5c-.1-.1-.2-.2-.4-.3-.3-.3-.6-.5-.8-.8-.2-.2-.4-.3-.6-.5-.3-.3-.7-.6-1-.8-.2-.1-.4-.3-.6-.4-.3-.2-.7-.5-1.1-.7-.2-.1-.3-.2-.5-.3-.5-.3-.9-.6-1.4-.8-.1 0-.1-.1-.2-.1-.5-.2-.9-.5-1.4-.7-.1-.1-.2-.1-.3-.2-1.1-.5-2.3-.9-3.5-1.2h-.1c-2.3-.6-4.7-1-7.1-1h-135c-46.6 0-84.5 37.9-84.5 84.5v18.5l53.7 53.7v-72.2c0-17 13.8-30.8 30.8-30.8H31v116.8c0 46.6 37.9 84.5 84.5 84.5h116.8v274.8c0 17-13.8 30.8-30.8 30.8H-77.1c-17 0-30.8-13.8-30.8-30.8V869.2l-13.7-13.7-40-40v198.9c0 46.6 37.9 84.5 84.5 84.5h278.6c46.6 0 84.5-37.9 84.5-84.5V712.8c-.1-.5-.1-.9-.1-1.3z"/></g></svg>
`;

/* tslint:disable */
const leagueTime = `
<svg class="u-svg-text" xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><g><path d="M1.1 485c-170.3-.1-308.2 137.5-308.1 307.4.1 169.4 137.9 307.1 307.3 307.1s307.2-137.8 307.2-307.2C307.6 623.2 169.9 485.1 1.1 485zm-2.4 563.4c-139.8-.1-254.6-115.7-254.6-256.2 0-141.2 115-256.1 256.3-256.1 141.2 0 256 115.1 256 256.4-.1 141.6-115.4 256-257.7 255.9z"/><path d="M23.4 782.7V593.9c0-12.8-10.4-23.1-23.1-23.1-12.8 0-23.1 10.3-23.1 23.1v198.3c0 2.3.3 4.5 1 6.6.2.7.5 1.4.7 2.1v.1c.3.7.6 1.3.9 1.9 0 0 0 .1.1.1.3.6.7 1.2 1.1 1.8l.1.1c.4.6.8 1.2 1.3 1.8 0 0 0 .1.1.1.5.6 1 1.2 1.6 1.8l98.6 98.7c4.5 4.5 10.4 6.8 16.4 6.8 5.9 0 11.8-2.2 16.4-6.8 9-9 9-23.7 0-32.7l-92.1-91.9z"/></g></svg>
`;

/* tslint:disable */
const leagueWait = `
<svg class="u-svg-text"  xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><g><path d="M218.3 1098.1h-437.4c-7.4 0-14.2-4.1-17.8-10.6-3.5-6.5-3.2-14.5.8-20.7l178.8-274.4-178.8-275.2c-4-6.2-4.4-14.2-.8-20.7 3.5-6.5 10.4-10.6 17.8-10.6h437.4c7.4 0 14.2 4.1 17.8 10.6 3.5 6.5 3.2 14.5-.8 20.7L56.5 791.6l178.8 275.2c4 6.2 4.4 14.2.8 20.7-3.5 6.5-10.4 10.6-17.8 10.6zm-400.1-40.5h362.9l-163.7-252c-.5-.5-1-1.1-1.4-1.7-5-6.8-5.5-15.8-.9-22.9L181 526.4h-362.9l163.7 252c.5.6 1 1.1 1.5 1.8 4.9 6.8 5.4 15.8.9 22.8l-166 254.6z"/><path d="M3.7 925.8c-65.5 0-119.5 48.6-128.4 111.6h256.8c-8.8-63-62.9-111.6-128.4-111.6z"/></g></svg>
`;

/* tslint:disable */
const labelEslCommunity = `
<svg class="u-svg-text"  xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><path fill-rule="evenodd" clip-rule="evenodd" d="M1.1 486c-169.8-.1-307.2 137-307.1 306.4.1 168.8 137.4 306.1 306.3 306 168.8 0 306.1-137.3 306.2-306.1 0-168.6-137.2-306.2-305.4-306.3zm-2.6 580.4c-149.6-.1-272.5-123.8-272.5-274.2C-273.9 641-150.9 518 .4 518.1c151.1 0 274 123.2 274 274.4-.1 151.5-123.5 274-275.9 273.9z"/><path d="M190.5 721.6H72.2V676c0-9.9-8-18-18-18h-232.4c-9.9 0-18 8-18 18v156.4c0 9.9 8 18 18 18h52.4l-8.9 64.2 74.5-64.2h21.5v33.1c0 12.7 10.3 23 23 23h99.1l78.2 67.4-9.3-67.4h38.4c12.7 0 23-10.3 23-23V744.6c-.3-12.7-10.6-23-23.2-23zm8.9 161.8c0 4.9-4 9-9 9H136l6.5 46.6-54-46.6H-15.8c-4.9 0-9-4-9-9V744.6c0-4.9 4-9 9-9h206.3c4.9 0 9 4 9 9v138.8z"/></svg>
`;

/* tslint:disable */
const leagueBracket = `
<svg class="u-svg-text"  xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><path d="M268.8 681.1H77.7c-10.6 0-20.3 4-27.6 10.6l-86.7-55.4v-55c0-22.6-18.4-41.1-41.1-41.1h-191.1c-22.6 0-41.1 18.4-41.1 41.1V719c0 22.6 18.4 41.1 41.1 41.1h191.1c22.6 0 41.1-18.4 41.1-41.1v-22.8L36.6 743v96l-73.2 46.8V863c0-22.6-18.4-41.1-41.1-41.1h-191.1c-22.6 0-41.1 18.4-41.1 41.1v137.7c0 22.6 18.4 41.1 41.1 41.1h191.1c22.6 0 41.1-18.4 41.1-41.1v-55l86.7-55.4c7.3 6.6 17 10.7 27.6 10.7h191.1c22.6 0 41.1-18.4 41.1-41.1V722.1c-.1-22.6-18.5-41-41.1-41zm-355.9 28.6h-172.3v-119h172.3v119zm0 281.6h-172.3v-119h172.3v119zm346.5-140.8H87.1v-119h172.3v119z"/></svg>
`;

/* tslint:disable */
const leagueDate = `
<svg class="u-svg-text"  xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><g><path d="M-144 598.3c13.3 0 24.1-10.8 24.1-24.1v-65.1c0-13.3-10.8-24.1-24.1-24.1s-24.1 10.8-24.1 24.1v65.1c0 13.3 10.8 24.1 24.1 24.1zM138.1 598.3c13.3 0 24.1-10.8 24.1-24.1v-65.1c0-13.3-10.8-24.1-24.1-24.1S114 495.8 114 509.1v65.1c0 13.3 10.8 24.1 24.1 24.1z"/><path d="M235.8 530.8H189v43.4c0 28-22.8 50.9-50.9 50.9-28.1 0-50.9-22.8-50.9-50.9v-43.4H-93.1v43.4c0 28-22.8 50.9-50.9 50.9-28 0-50.9-22.8-50.9-50.9v-43.4h-45c-36.9 0-66.9 29.9-66.9 66.9v429.1c0 37 30 66.9 66.9 66.9h475.6c37 0 66.9-29.9 66.9-66.9V597.7c.1-37-29.9-66.9-66.8-66.9zm20 496c0 11-9 20-20.1 20h-475.6c-11.1 0-20.1-9-20.1-20V698.4h515.8v328.4z"/><path d="M-210.8 904.2h53.4c10.3 0 18.7-8.4 18.7-18.7v-24.9c0-10.3-8.4-18.7-18.7-18.7h-53.4c-10.3 0-18.7 8.4-18.7 18.7v24.9c0 10.3 8.4 18.7 18.7 18.7zM-89.4 904.2H-36c10.3 0 18.7-8.4 18.7-18.7v-24.9c0-10.3-8.4-18.7-18.7-18.7h-53.4c-10.3 0-18.7 8.4-18.7 18.7v24.9c0 10.3 8.3 18.7 18.7 18.7zM32 904.2h53.4c10.3 0 18.7-8.4 18.7-18.7v-24.9c0-10.3-8.3-18.7-18.7-18.7H32c-10.3 0-18.7 8.4-18.7 18.7v24.9c-.1 10.3 8.3 18.7 18.7 18.7zM153.3 904.2h53.4c10.3 0 18.7-8.4 18.7-18.7v-24.9c0-10.3-8.4-18.7-18.7-18.7h-53.4c-10.3 0-18.7 8.4-18.7 18.7v24.9c0 10.3 8.4 18.7 18.7 18.7zM-89.4 809.5H-36c10.3 0 18.7-8.4 18.7-18.7V766c0-10.3-8.4-18.7-18.7-18.7h-53.4c-10.3 0-18.7 8.4-18.7 18.7v24.9c0 10.3 8.3 18.6 18.7 18.6zM32 809.5h53.4c10.3 0 18.7-8.4 18.7-18.7V766c0-10.3-8.3-18.7-18.7-18.7H32c-10.3 0-18.7 8.4-18.7 18.7v24.9c-.1 10.3 8.3 18.6 18.7 18.6zM153.3 809.5h53.4c10.3 0 18.7-8.4 18.7-18.7V766c0-10.3-8.4-18.7-18.7-18.7h-53.4c-10.3 0-18.7 8.4-18.7 18.7v24.9c0 10.3 8.4 18.6 18.7 18.6zM-210.8 998.8h53.4c10.3 0 18.7-8.4 18.7-18.7v-24.9c0-10.3-8.4-18.7-18.7-18.7h-53.4c-10.3 0-18.7 8.4-18.7 18.7v24.9c0 10.3 8.4 18.7 18.7 18.7zM-89.4 998.8H-36c10.3 0 18.7-8.4 18.7-18.7v-24.9c0-10.3-8.4-18.7-18.7-18.7h-53.4c-10.3 0-18.7 8.4-18.7 18.7v24.9c0 10.3 8.3 18.7 18.7 18.7zM32 998.8h53.4c10.3 0 18.7-8.4 18.7-18.7v-24.9c0-10.3-8.3-18.7-18.7-18.7H32c-10.3 0-18.7 8.4-18.7 18.7v24.9c-.1 10.3 8.3 18.7 18.7 18.7z"/></g></svg>
`;

/* tslint:disable */
const replacement = `
<svg class="u-svg-text" xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><path d="M270.7 519.1c-22.1-22.1-51.5-34.3-82.8-34.3h-380.3c-64.6 0-117.1 52.5-117.1 117.1v380.3c0 31.3 12.2 60.7 34.3 82.8 22.1 22.1 51.5 34.3 82.8 34.3h380.3c64.6 0 117.1-52.5 117.1-117.1V601.8c0-31.2-12.2-60.6-34.3-82.7zm-514.6 82.7c0-28.4 23.1-51.5 51.5-51.5h380.3c1.7 0 3.3.1 4.9.2L-243.7 987c-.2-1.6-.2-3.3-.2-4.9V601.8z"/></svg>
`;

/* tslint:disable */
const leagueLeagueInfo = `
<svg class="u-svg-text" xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><path d="M216.2 693.7L15.4 492.9c-5-5-11.8-7.9-18.9-7.9h-134.7c-46.5 0-84.3 37.8-84.3 84.3v444.2c0 46.5 37.8 84.3 84.3 84.3h278c46.5 0 84.3-37.8 84.3-84.3V712.6c0-7.1-2.8-13.9-7.9-18.9zm-45.7 319.8c0 16.9-13.8 30.7-30.7 30.7h-278c-16.9 0-30.7-13.8-30.7-30.7V569.3c0-16.9 13.8-30.7 30.7-30.7h123.6l11.1 11.1v105.4c0 31.8 25.8 57.5 57.5 57.5h105.4l11.1 11.1v289.8z"/></svg>
`;

/* tslint:disable */
const leagueMatch = `
<svg class="u-svg-text"   xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><g><path d="M177.1 528.8L25 680.8l93.4 93.4 152.1-152L305.7 490M-113.8 819.7l-47.6 47.5-49.8-49.8-44.4 44.4 73.2 73.2-109.5 109.4 42.2 42.2 109.4-109.5 74.2 74.2 44.4-44.4-46.3-46.3 47.6-47.5"/><path d="M290.6 1039.4L181.2 930l73.1-73.2-44.4-44.4-49.8 49.8-47.6-47.6-290.9-290.9L-307 485l35.2 132.1L19.1 908l47.6 47.6-46.2 46.2 44.4 44.4 74.1-74.1 109.5 109.5"/></g></svg>
`;

/* tslint:disable */
const leagueRequirements = `
<svg class="u-svg-text"  xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><path d="M304.5 509.6c-.2-.8-.4-1.7-.7-2.5-.5-1.6-1.2-3.2-2.1-4.7-.4-.8-.9-1.5-1.4-2.2-1.3-1.8-2.7-3.5-4.5-5-.3-.3-.7-.6-1.1-.9-.6-.4-1.1-.8-1.7-1.2-.1-.1-.2-.1-.3-.2-.6-.4-1.1-.7-1.7-1-.1-.1-.2-.1-.3-.2l-1.8-.9h-.1c-.6-.3-1.2-.5-1.8-.7-.1 0-.2-.1-.3-.1-.6-.2-1.2-.4-1.8-.5-.1 0-.2-.1-.4-.1-.6-.2-1.3-.3-1.9-.4h-.1c-.6-.1-1.2-.2-1.8-.2h-.3c-.6 0-1.2-.1-1.9-.1h-1.9c-.3 0-.6 0-.9.1-.2 0-.3 0-.5.1-.3 0-.7.1-1 .1-.1 0-.2 0-.4.1-.5.1-.9.2-1.4.3-.1 0-.2 0-.3.1-.4.1-.7.2-1.1.3-.2 0-.3.1-.5.1l-.9.3c-.2.1-.3.1-.5.2-.3.1-.6.2-.8.3-.2.1-.3.1-.5.2-.3.1-.5.2-.8.4-.2.1-.3.1-.5.2-.3.1-.6.3-.9.5-.1.1-.2.1-.4.2-.4.2-.8.5-1.2.7-.1 0-.1.1-.2.1-.3.2-.7.5-1 .7-.1.1-.3.2-.4.3-.3.2-.5.4-.8.6-.1.1-.3.2-.4.3-.2.2-.5.4-.7.6l-.4.4c-.2.2-.4.4-.7.6l-.4.4-.7.7c-.1.1-.2.2-.3.4-.3.4-.6.8-1 1.2l-51 65.3c-10.6-11.4-25.6-18.5-42.4-18.5h-414.2c-31.8 0-57.7 25.9-57.7 57.7v436.6c0 31.8 25.9 57.7 57.7 57.7h414.2c31.8 0 57.7-25.9 57.7-57.7V632l78.1-100.1c2.3-2.9 3.9-6.2 4.8-9.5.1-.4.2-.8.3-1.3.4-1.7.6-3.4.6-5.1.1-2.2-.1-4.3-.6-6.4zm-129.8 530.7c0 6.1-5 11-11 11h-414.2c-6.1 0-11-5-11-11V603.7c0-6.1 4.9-11 11-11h414.2c6.1 0 11 5 11 11v1L-47 888.8l-113.2-141.5c-.3-.4-.6-.8-1-1.1-.1-.1-.2-.2-.3-.4l-.7-.7-.4-.4c-.2-.2-.4-.4-.7-.6l-.4-.4c-.2-.2-.4-.4-.7-.6-.2-.1-.3-.2-.5-.4s-.5-.4-.7-.5c-.2-.1-.3-.2-.5-.3-.3-.2-.5-.4-.8-.5-.1-.1-.3-.2-.4-.3-.4-.2-.8-.5-1.2-.7 0 0-.1 0-.1-.1-.4-.2-.7-.4-1.1-.6-.1-.1-.3-.1-.4-.2-.3-.1-.6-.2-.8-.4-.2-.1-.3-.1-.5-.2-.3-.1-.5-.2-.8-.3-.2-.1-.4-.1-.5-.2-.3-.1-.5-.2-.8-.2-.2-.1-.4-.1-.5-.2-.3-.1-.6-.1-.8-.2-.2 0-.3-.1-.5-.1-.3-.1-.6-.1-.9-.2-.1 0-.3-.1-.4-.1-.5-.1-.9-.1-1.4-.2h-.2c-.4 0-.8-.1-1.2-.1H-181.2c-.7 0-1.3 0-2 .1s-1.3.1-2 .2h-.3c-.6.1-1.3.2-1.9.4-.1 0-.2.1-.4.1-.6.2-1.3.3-1.9.6-.1 0-.1.1-.2.1-.6.2-1.2.4-1.8.7-.1 0-.1.1-.2.1l-1.8.9c-.1.1-.2.1-.3.2-.6.3-1.2.7-1.7 1-.1.1-.2.1-.3.2-.6.4-1.2.8-1.7 1.3-11.6 9.2-13.4 26.1-4.2 37.7l134.4 167.9c1 1.2 2 2.3 3.1 3.3 4.9 4.3 11.2 6.8 17.8 6.8h.2c6.2 0 12-2.2 16.7-6 .4-.3.8-.6 1.1-1 .7-.7 1.5-1.4 2.1-2.2.3-.4.7-.8 1-1.2l200.3-256.7v348.7z"/></svg>
`;

/* tslint:disable */
const leagueParticipants = `
<svg class="u-svg-text"  xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><path d="M81.3 892.7c-2.4-26.5-1.5-12.7-1.5-37 19.9-6.9 33.6-46.5 37.2-80.5 9.5-.8 24.4-10 28.7-46.5 3.2-30.5-7-30.6-12.7-34 15.4-46.3 30.8-176.8-59.1-180-10.9-19.2-39-29-75.5-29-35.5.7-63.5 7.5-84.9 19.3-4.2 2.3-11.7 13.1-11.7 13.1s-9.7 1.8-13.8 6.1c-41.7 42.5-41.7 91.1-21.1 170.5-5.7 3.5-16 3.5-12.7 34 4.4 36.4 19.3 45.6 28.7 46.5 3.6 34 26.1 74.2 38.1 80.5 0 24.3.9 10.5-1.5 37-28.9 77.6-232.7 6.1-232.7 205.6H313c.1-206-202.8-127.9-231.7-205.6z"/></svg>
`;

/* tslint:disable */
const leagueRules = `
<svg class="u-svg-text"  xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><path d="M177.6 627.4l-13 28.8H20.1v-54.3c0-11.1-9-20.1-20.1-20.1s-20.1 9-20.1 20.1v54.3h-144.5l-13-28.8-128.6 284.7v4.3c0 48.1 56.5 85.7 128.6 85.7S-49 964.6-49 916.5v-4.3l-97.5-215.7h126.3v181.2c0 11.1 9 20.1 20.1 20.1s20.1-9 20.1-20.1V696.4h126.3L49 912.2v4.3c0 48.1 56.5 85.7 128.6 85.7s128.6-37.7 128.6-85.7v-4.3L177.6 627.4zm-355.2 97.8l77.3 171.2H-255l77.4-171.2zm0 236.8c-35.8 0-64.1-11.3-78.3-25.4h156.7c-14.3 14.1-42.6 25.4-78.4 25.4zM255 896.4H100.3l77.3-171.2L255 896.4zM177.6 962c-35.8 0-64.1-11.3-78.3-25.4H256c-14.3 14.1-42.6 25.4-78.4 25.4z"/></svg>
`;

/* tslint:disable */
const leagueSeries = `
<svg class="u-svg-text" xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><path d="M265 869.5h-23.4v-49.4c0-25.2-20.5-45.7-45.7-45.7H19.7v-60.1h23.4c21.9 0 39.8-17.9 39.8-39.8V587c0-21.9-17.9-39.8-39.8-39.8h-87.5c-21.9 0-39.8 17.9-39.8 39.8v87.5c0 21.9 17.9 39.8 39.8 39.8H-21v60.1h-175.6c-25.2 0-45.7 20.5-45.7 45.7v49.4h-23.4c-21.9 0-39.8 17.9-39.8 39.8v87.5c0 21.9 17.9 39.8 39.8 39.8h87.5c21.9 0 39.8-17.9 39.8-39.8v-87.5c0-21.9-17.9-39.8-39.8-39.8h-23.4v-49.4c0-2.6 2.2-4.9 4.9-4.9h175.6v54.2h-23c-21.9 0-39.8 17.9-39.8 39.8v87.5c0 21.9 17.9 39.8 39.8 39.8h87.5c21.9 0 39.8-17.9 39.8-39.8v-87.5c0-21.9-17.9-39.8-39.8-39.8H19.7v-54.2H196c2.6 0 4.9 2.2 4.9 4.9v49.4h-23.4c-21.9 0-39.8 17.9-39.8 39.8v87.5c0 21.9 17.9 39.8 39.8 39.8H265c21.9 0 39.8-17.9 39.8-39.8v-87.5c0-21.9-17.8-39.8-39.8-39.8zM-43.4 588.1h85.5v85.5h-85.5v-85.5zm-135.8 407.8h-85.5v-85.5h85.5v85.5zm221.6 0h-85.5v-85.5h85.5v85.5zm221.6 0h-85.5v-85.5H264v85.5z"/></svg>
`;

/* tslint:disable */
const leaguePrizepool = `
<svg class="u-svg-text"  xmlns="http://www.w3.org/2000/svg" viewBox="-400 392 800 800" enable-background="new -400 392 800 800"><path d="M-135.5 661.1c-96.2 88.2-169.6 424-61 424 186 0 204.2 2.5 393.1 0 122.7-1.6 26.1-341.8-96.6-441-67.2-54.5-165.1-47.6-235.5 17z"/><path d="M96.1 639.1l3-3c-1.6-3.1-2.2-6.6-1.9-10.1-43.3 5.6-87.5 4.7-130.6-2.6-22.6-3.8-36.8-3.1-46.2-24.7-8.6-19.8-17-39.8-25.5-59.7-3.1-7.3-6.3-15.4-3.5-22.9 1.6-4.3 5.1-7.8 9-10.4 13.5-8.8 31.4-7.5 47-3.2s30.7 11.1 46.9 12.4C8.7 516 23 512.7 37 509.5c6.2-1.4 13.4-2.6 18.4 1.3 4.1 3.2 5.2 8.7 8.2 12.8 5.7 7.7 16.8 9.1 26.6 8.9 31.4-.6 63.3-10.8 93.8-3.3 5.4 1.3 11.5 4.1 12.5 9.4.9 4.7-2.6 8.9-5.9 12.5-28.4 30.6-59 59.2-91.6 85.6"/><path d="M104.6 647.6l3-3c4.1-4.1 4.1-9.6 1.9-14.5-.6-1.4-.3-2.6-.2-4 .3-5.9-5.8-12.8-12-12-48.4 6.2-97.6 4.3-145.5-5-1.8-.4-3.7-.8-5.5-1.3-.7-.2-5.4-1.9-3.1-.9-1.6-.7-3-1.6-4.5-2.5-.4-.3-1.6-1.4-.1 0-1-.8-1.8-1.7-2.7-2.6-.3-.4-1.7-2.3-.6-.7-.5-.7-.9-1.4-1.4-2.1-1.2-2-2.2-4-3.2-6.2-3-6.6-5.7-13.2-8.6-19.9-5.2-12.1-10.3-24.1-15.4-36.2-1.8-4.1-4-8.6-4.4-13.1-.4-4 2-6.2 5.4-8.2 18.5-11 43.1 1.2 61.4 6.7 12.4 3.8 25.2 6 38.2 4.9 6.4-.5 12.7-1.5 19-2.7 6-1.2 12.1-3.3 18.2-3.9 1-.1 2.9-.2 3.8.2-1.6-.9.3 1 .8 1.9 2.5 4.6 4.7 9.1 8.7 12.7 8.6 7.7 20.5 9.6 31.6 9.5 25-.2 49.5-6.9 74.6-6 6.1.2 12.1.9 18 2.6-.2-.1 3.7 1.5 2.1.7.7.3 1.8.5 1.7 1.1 0 .1-1.3-2.8-1.2-2.7-.7-.3.2 0-.9.6-2.4 1.3-4.8 5.2-6.7 7.3l-7.8 8.1c-4.8 4.9-9.6 9.8-14.5 14.6-20.5 20.2-41.9 39.3-64.2 57.4-5 4.1-4.3 12.7 0 17 5 5 11.9 4.1 17 0 21.8-17.7 42.8-36.4 62.9-56.1 9.8-9.6 19.5-19.3 28.7-29.5 4.9-5.5 9.5-11.7 9.5-19.4 0-7.6-4.4-14.2-10.7-18.1-11-7-26.7-7.7-39.3-7.6-13.6.1-27.1 2.1-40.6 3.7-11.3 1.4-25.8 3.7-36 2-2.5-.4-4.9-1.1-6.3-1.8-1.1-.6-2.1-1.4-2.5-2-4.1-5.6-5.5-11.8-11.8-16-11.7-7.9-26.1-2.7-38.7-.1-12.6 2.6-25.1 3.8-37.7.9-25.7-5.9-50.8-19.8-77.8-11.8-12.7 3.8-25.6 12.8-28.2 26.6-2.8 14.6 5.5 28.6 11 41.5 5.8 13.6 11.5 27.2 17.4 40.7 4.6 10.5 9.5 20 19.3 26.5 8.4 5.6 18.8 7.2 28.6 8.6 15.3 2.3 30.5 4.8 45.9 6.2 32 2.9 64.3 2.2 96.2-1.9l-12-12c-.3 5.8 1.2 10.9 3.5 16.1.6-4.8 1.3-9.7 1.9-14.5l-3 3c-11.2 11.2 5.8 28.1 16.8 17.2z"/><text transform="translate(-85.54 962.963)" fill="#FFF" font-family="'OpenSans-Semibold'" font-size="300">$</text></svg>
`;

/**
 * For SSR (server side rendering) this is not safe.
 * @see: https://github.com/angular/angular/issues/15008#issuecomment-285141070)
 */
class AutofocusDirective {
    constructor(el) {
        this.el = el;
        this.focus = true;
    }
    set autofocus(condition) {
        this.focus = condition !== false; // only set to false if specifiedSS
    }
    ngOnInit() {
        if (this.focus) {
            window.setTimeout(() => this.el.nativeElement.focus());
        }
    }
}
AutofocusDirective.ɵfac = function AutofocusDirective_Factory(t) { return new (t || AutofocusDirective)(i0.ɵɵdirectiveInject(i0.ElementRef)); };
AutofocusDirective.ɵdir = i0.ɵɵdefineDirective({ type: AutofocusDirective, selectors: [["", "autofocus", ""]], inputs: { autofocus: "autofocus" } });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AutofocusDirective, [{
        type: Directive,
        args: [{
                selector: "[autofocus]",
            }]
    }], function () { return [{ type: i0.ElementRef }]; }, { autofocus: [{
            type: Input
        }] }); })();

const _c0$6 = function (a0) { return [a0]; };
class GameIconComponent {
}
GameIconComponent.ɵfac = function GameIconComponent_Factory(t) { return new (t || GameIconComponent)(); };
GameIconComponent.ɵcmp = i0.ɵɵdefineComponent({ type: GameIconComponent, selectors: [["game-icon"]], inputs: { gameId: "gameId" }, decls: 1, vars: 3, consts: [[3, "ngClass"]], template: function GameIconComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelement(0, "div", 0);
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(1, _c0$6, "icon-game-" + ctx.gameId));
    } }, directives: [i1$1.NgClass], styles: [""], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GameIconComponent, [{
        type: Component,
        args: [{
                selector: "game-icon",
                templateUrl: "./game-icon.component.html",
                styleUrls: ["./game-icon.component.scss"],
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { gameId: [{
            type: Input
        }] }); })();

const _c0$5 = function (a0, a1) { return [a0, a1]; };
class SkillIconComponent {
    ngOnChanges() {
        // class suffixes are with pro_qualifier instead of pro-qualifier
        this.skillLevel = this.level === "pro-qualifier" ? "pro_qualifier" : this.level;
    }
}
SkillIconComponent.ɵfac = function SkillIconComponent_Factory(t) { return new (t || SkillIconComponent)(); };
SkillIconComponent.ɵcmp = i0.ɵɵdefineComponent({ type: SkillIconComponent, selectors: [["skill-icon"]], inputs: { level: "level" }, features: [i0.ɵɵNgOnChangesFeature], decls: 1, vars: 4, consts: [[1, "esl-icon", 3, "ngClass"]], template: function SkillIconComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelement(0, "div", 0);
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(1, _c0$5, "esl_label-esl-" + ctx.skillLevel, "text-color-esl-" + ctx.skillLevel));
    } }, directives: [i1$1.NgClass], styles: ["[_nghost-%COMP%]{margin-top:-2px}"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SkillIconComponent, [{
        type: Component,
        args: [{
                selector: "skill-icon",
                templateUrl: "./skill-icon.component.html",
                styleUrls: ["./skill-icon.component.scss"],
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { level: [{
            type: Input
        }] }); })();

function EmptyCallActionComponent_play_button_3_Template(rf, ctx) { if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "play-button", 3);
    i0.ɵɵlistener("clicked", function EmptyCallActionComponent_play_button_3_Template_play_button_clicked_0_listener() { i0.ɵɵrestoreView(_r2); const ctx_r1 = i0.ɵɵnextContext(); return ctx_r1.action.emit(); });
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(ctx_r0.actionLabel);
} }
const _c0$4 = ["*"];
class EmptyCallActionComponent extends InheritClassComponent {
    constructor() {
        super(...arguments);
        this.action = new EventEmitter();
    }
}
EmptyCallActionComponent.ɵfac = function EmptyCallActionComponent_Factory(t) { return ɵEmptyCallActionComponent_BaseFactory(t || EmptyCallActionComponent); };
EmptyCallActionComponent.ɵcmp = i0.ɵɵdefineComponent({ type: EmptyCallActionComponent, selectors: [["empty-call-action"]], inputs: { actionLabel: "actionLabel" }, outputs: { action: "action" }, features: [i0.ɵɵInheritDefinitionFeature], ngContentSelectors: _c0$4, decls: 4, vars: 2, consts: [[1, "c-empty-call-action", "o-whisper-box", 3, "ngClass"], [1, "o-whisper-box__description", "u-baseline"], ["class", "c-button--primary c-button--huge", 3, "clicked", 4, "ngIf"], [1, "c-button--primary", "c-button--huge", 3, "clicked"]], template: function EmptyCallActionComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵprojection(2);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(3, EmptyCallActionComponent_play_button_3_Template, 2, 1, "play-button", 2);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", ctx.classList);
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("ngIf", ctx.actionLabel);
    } }, directives: [i1$1.NgClass, i1$1.NgIf, ButtonComponent], styles: [""], changeDetection: 0 });
const ɵEmptyCallActionComponent_BaseFactory = /*@__PURE__*/ i0.ɵɵgetInheritedFactory(EmptyCallActionComponent);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(EmptyCallActionComponent, [{
        type: Component,
        args: [{
                selector: "empty-call-action",
                templateUrl: "./empty-call-action.component.html",
                styleUrls: ["./empty-call-action.component.scss"],
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { action: [{
            type: Output
        }], actionLabel: [{
            type: Input
        }] }); })();

const _c0$3 = ["*"];
class PlayHeaderComponent {
}
PlayHeaderComponent.ɵfac = function PlayHeaderComponent_Factory(t) { return new (t || PlayHeaderComponent)(); };
PlayHeaderComponent.ɵcmp = i0.ɵɵdefineComponent({ type: PlayHeaderComponent, selectors: [["play-header"]], inputs: { headerTitle: "headerTitle", userPage: "userPage" }, ngContentSelectors: _c0$3, decls: 5, vars: 3, consts: [[1, "c-play-header"], [1, "c-play-header__title"], [1, "c-play-header__addons"]], template: function PlayHeaderComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "h1", 1);
        i0.ɵɵtext(2);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(3, "div", 2);
        i0.ɵɵprojection(4);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵclassProp("c-play-header--user", ctx.userPage);
        i0.ɵɵadvance(2);
        i0.ɵɵtextInterpolate1(" ", ctx.headerTitle, " ");
    } }, encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PlayHeaderComponent, [{
        type: Component,
        args: [{
                selector: "play-header",
                templateUrl: "./play-header.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { headerTitle: [{
            type: Input
        }], userPage: [{
            type: Input
        }] }); })();

/* tslint:disable */
const trustedIcon = `
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="#1076E5"/><path d="M6.5 10.5L11 15l7-7" stroke="#fff" stroke-width="2" filter="url(#filter0_d)"/>
    <defs>
      <filter id="filter0_d" x="5.793" y="7.293" width="12.914" height="10.121" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
        <feColorMatrix in="SourceAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/>
        <feOffset dy="1"/>
        <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
        <feBlend in2="BackgroundImageFix" result="effect1_dropShadow"/><feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
      </filter>
    </defs>
  </svg>
`;

/* tslint:disable */
const premiumIcon = `
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10" fill="#FF6D00"/><path d="M10.86 13.913V18H9V7h4.096c1.196 0 2.144.32 2.845.96C16.647 8.6 17 9.444 17 10.497c0 1.078-.346 1.916-1.037 2.516-.686.6-1.65.899-2.89.899H10.86zm0-1.534h2.236c.661 0 1.166-.159 1.514-.476.348-.322.522-.786.522-1.39 0-.594-.176-1.068-.53-1.42-.352-.358-.837-.542-1.455-.552H10.86v3.838z" fill="#fff" filter="url(#filter0_d)"/>
    <defs>
      <filter id="filter0_d" x="9" y="7" width="8" height="12" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/>
        <feColorMatrix in="SourceAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/>
        <feOffset dy="1"/><feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
        <feBlend in2="BackgroundImageFix" result="effect1_dropShadow"/>
        <feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
      </filter>
    </defs>
  </svg>
`;

/* tslint:disable */
const settingsIcon = `
<svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
  <path d="M12.76 8.624c.024-.2.04-.408.04-.624 0-.216-.016-.424-.048-.624l1.352-1.056a.33.33 0 0 0 .08-.408l-1.28-2.216a.325.325 0 0 0-.392-.144l-1.592.64a4.687 4.687 0 0 0-1.08-.624L9.6 1.872a.32.32 0 0 0-.32-.272H6.72a.311.311 0 0 0-.312.272l-.24 1.696c-.392.16-.752.376-1.08.624l-1.592-.64a.316.316 0 0 0-.392.144l-1.28 2.216a.31.31 0 0 0 .08.408l1.352 1.056c-.032.2-.056.416-.056.624 0 .208.016.424.048.624L1.896 9.68a.33.33 0 0 0-.08.408l1.28 2.216c.08.144.248.192.392.144l1.592-.64c.336.256.688.464 1.08.624l.24 1.696c.032.16.16.272.32.272h2.56c.16 0 .296-.112.312-.272l.24-1.696c.392-.16.752-.376 1.08-.624l1.592.64c.144.056.312 0 .392-.144l1.28-2.216a.31.31 0 0 0-.08-.408L12.76 8.624zM8 10.4c-1.32 0-2.4-1.08-2.4-2.4 0-1.32 1.08-2.4 2.4-2.4 1.32 0 2.4 1.08 2.4 2.4 0 1.32-1.08 2.4-2.4 2.4z"/>
</svg>
`;

/* tslint:disable */
const close = `
    <svg class="u-svg-text" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"/>
    </svg>
`;

const _c0$2 = function (a0) { return { "top.px": a0 }; };
class TooltipComponent {
    constructor(element, cdr) {
        this.element = element;
        this.cdr = cdr;
    }
    ngAfterViewInit() {
        this.top = -this.element.nativeElement.children[0].offsetHeight;
        this.cdr.detectChanges();
    }
}
TooltipComponent.ɵfac = function TooltipComponent_Factory(t) { return new (t || TooltipComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); };
TooltipComponent.ɵcmp = i0.ɵɵdefineComponent({ type: TooltipComponent, selectors: [["play-tooltip"]], inputs: { content: "content" }, decls: 2, vars: 4, consts: [[1, "c-tooltip", 3, "ngStyle"]], template: function TooltipComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵtext(1);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngStyle", i0.ɵɵpureFunction1(2, _c0$2, ctx.top));
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate1(" ", ctx.content, "\n");
    } }, directives: [i1$1.NgStyle], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TooltipComponent, [{
        type: Component,
        args: [{
                selector: "play-tooltip",
                templateUrl: "./tooltip.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }]; }, { content: [{
            type: Input
        }] }); })();

class TooltipDirective {
    constructor(viewContainerRef, resolver) {
        this.viewContainerRef = viewContainerRef;
        this.resolver = resolver;
    }
    onMouseEnter() {
        const factory = this.resolver.resolveComponentFactory(TooltipComponent);
        this.tooltip = this.viewContainerRef.createComponent(factory);
        this.tooltip.instance.content = this.content;
    }
    onMouseLeave() {
        if (this.tooltip) {
            this.tooltip.destroy();
        }
    }
}
TooltipDirective.ɵfac = function TooltipDirective_Factory(t) { return new (t || TooltipDirective)(i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(i0.ComponentFactoryResolver)); };
TooltipDirective.ɵdir = i0.ɵɵdefineDirective({ type: TooltipDirective, selectors: [["", "tooltip", ""]], hostBindings: function TooltipDirective_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("mouseenter", function TooltipDirective_mouseenter_HostBindingHandler() { return ctx.onMouseEnter(); })("mouseleave", function TooltipDirective_mouseleave_HostBindingHandler() { return ctx.onMouseLeave(); });
    } }, inputs: { content: ["tooltip", "content"] } });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TooltipDirective, [{
        type: Directive,
        args: [{ selector: "[tooltip]" }]
    }], function () { return [{ type: i0.ViewContainerRef }, { type: i0.ComponentFactoryResolver }]; }, { content: [{
            type: Input,
            args: ["tooltip"]
        }], onMouseEnter: [{
            type: HostListener,
            args: ["mouseenter"]
        }], onMouseLeave: [{
            type: HostListener,
            args: ["mouseleave"]
        }] }); })();

class FormatDateComponent {
    constructor(formatDate, config) {
        this.formatDate = formatDate;
        this.config = config;
    }
    ngOnInit() {
        this.short = this.onlyDate
            ? this.formatDate.formatDateTime(this.date, { showTimezone: false, showTime: false, timezone: this.config.timezone })
            : this.formatDate.formatDateTime(this.date, { showTimezone: false, timezone: this.config.timezone });
        this.full = this.formatDate.getFullDateTimeTz(this.date);
    }
}
FormatDateComponent.ɵfac = function FormatDateComponent_Factory(t) { return new (t || FormatDateComponent)(i0.ɵɵdirectiveInject(FormatDate), i0.ɵɵdirectiveInject(BASE_CONFIG)); };
FormatDateComponent.ɵcmp = i0.ɵɵdefineComponent({ type: FormatDateComponent, selectors: [["format-date"]], inputs: { date: "date", onlyDate: "onlyDate" }, decls: 2, vars: 2, consts: [[3, "tooltip"]], template: function FormatDateComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵtext(1);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("tooltip", ctx.full);
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate(ctx.short);
    } }, directives: [TooltipDirective], styles: ["[_nghost-%COMP%] {\n                display: inline-block;\n                position: relative;\n            }"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FormatDateComponent, [{
        type: Component,
        args: [{
                selector: "format-date",
                templateUrl: "./format-date.component.html",
                styles: [
                    `
            :host {
                display: inline-block;
                position: relative;
            }
        `,
                ],
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: FormatDate }, { type: undefined, decorators: [{
                type: Inject,
                args: [BASE_CONFIG]
            }] }]; }, { date: [{
            type: Input
        }], onlyDate: [{
            type: Input
        }] }); })();

class TrustedBadgeComponent {
}
TrustedBadgeComponent.ɵfac = function TrustedBadgeComponent_Factory(t) { return new (t || TrustedBadgeComponent)(); };
TrustedBadgeComponent.ɵcmp = i0.ɵɵdefineComponent({ type: TrustedBadgeComponent, selectors: [["trusted-badge"]], decls: 3, vars: 1, consts: [[1, "trusted-badge"], ["href", "/playercard_portal", 3, "tooltip"], ["src", "https://cdn.eslgaming.com/play/eslstatic/skins/v2008_base/content/16_trusted.gif", 1, "ranking__trusted-badge"]], template: function TrustedBadgeComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "a", 1);
        i0.ɵɵelement(2, "img", 2);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("tooltip", "ESL Trusted");
    } }, directives: [TooltipDirective], styles: ["[_nghost-%COMP%]{position:relative}"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TrustedBadgeComponent, [{
        type: Component,
        args: [{
                selector: "trusted-badge",
                templateUrl: "./trusted-badge.component.html",
                styleUrls: ["./trusted-badge.component.scss"],
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, null); })();

const _c0$1 = ["AppBanner"];
function AppBannerComponent_div_0_Template(rf, ctx) { if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 1, 2);
    i0.ɵɵelementStart(2, "div", 3);
    i0.ɵɵlistener("click", function AppBannerComponent_div_0_Template_div_click_2_listener() { i0.ɵɵrestoreView(_r3); const ctx_r2 = i0.ɵɵnextContext(); return ctx_r2.removeBanner(); });
    i0.ɵɵelementEnd();
    i0.ɵɵelement(3, "div", 4);
    i0.ɵɵelementStart(4, "div", 5);
    i0.ɵɵelementStart(5, "div", 6);
    i0.ɵɵtext(6, "ESL Play App");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(7, "div", 7);
    i0.ɵɵtext(8);
    i0.ɵɵpipe(9, "translate");
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(10, "a", 8);
    i0.ɵɵtext(11);
    i0.ɵɵpipe(12, "translate");
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(8);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(9, 3, ctx_r0.activeStore.subtitle));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("href", ctx_r0.activeStore.link, i0.ɵɵsanitizeUrl);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(12, 5, "play-app-banner:button"), " ");
} }
class AppBannerComponent {
    constructor() {
        this.displayBanner = false;
        this.activeStore = {
            link: "",
            subtitle: "",
        };
        this.googlePlay = {
            link: "https://play.google.com/store/apps/details?id=com.esl_tech.play",
            subtitle: "play-app-banner:subtitle-play-store",
        };
        this.appStore = {
            link: "https://apps.apple.com/us/app/esl-play/id1048144798",
            subtitle: "play-app-banner:subtitle-app-store",
        };
    }
    ngAfterViewInit() {
        if (!this.displayBanner) {
            return;
        }
        const appBanner = this.appBannerRef.nativeElement;
        const visualViewport = window["visualViewport"];
        const animateBanner = () => {
            if (this.displayBanner) {
                const margin = visualViewport.width * 0.05;
                appBanner.style.transform = `
              translate3d(
                ${(visualViewport.offsetLeft + margin).toFixed()}px,
                ${(visualViewport.offsetTop + visualViewport.height - (290 + margin)).toFixed()}px,
                0px)
              scale(${(1 / 1140) * visualViewport.width})
              `;
                setTimeout(() => {
                    window.requestAnimationFrame(animateBanner);
                }, 1000 / 60);
            }
        };
        window.requestAnimationFrame(animateBanner);
    }
    ngOnInit() {
        if (/android/i.test(navigator.userAgent)) {
            this.activeStore = this.googlePlay;
            this.displayBanner = true;
        }
        else if (/iPhone/.test(navigator.userAgent) && !window["MSStream"]) {
            this.activeStore = this.appStore;
            this.displayBanner = true;
        }
        if (!window["visualViewport"]) {
            this.displayBanner = false;
        }
        if (+localStorage.getItem("ExpireAppbanner") > Date.now()) {
            this.displayBanner = false;
        }
    }
    removeBanner() {
        const now = new Date();
        const expireDate = new Date().setDate(now.getDate() + 30).toString();
        this.displayBanner = false;
        this.appBannerRef.nativeElement.remove();
        localStorage.setItem("ExpireAppbanner", expireDate);
    }
}
AppBannerComponent.ɵfac = function AppBannerComponent_Factory(t) { return new (t || AppBannerComponent)(); };
AppBannerComponent.ɵcmp = i0.ɵɵdefineComponent({ type: AppBannerComponent, selectors: [["lib-app-banner"]], viewQuery: function AppBannerComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0$1, 1);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.appBannerRef = _t.first);
    } }, decls: 1, vars: 1, consts: [["id", "esl-smartbanner", "class", "smartbanner", 4, "ngIf"], ["id", "esl-smartbanner", 1, "smartbanner"], ["AppBanner", ""], [1, "smartbanner__close", 3, "click"], [1, "smartbanner__icon"], [1, "smartbanner__info"], [1, "smartbanner__info__title"], [1, "smartbanner__info__sub"], ["target", "_blank", "rel", "noopener", 1, "c-button", "c-button--primary", "smartbanner__button", 3, "href"]], template: function AppBannerComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, AppBannerComponent_div_0_Template, 13, 7, "div", 0);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.displayBanner);
    } }, directives: [i1$1.NgIf], pipes: [i1.TranslatePipe], styles: [".smartbanner[_ngcontent-%COMP%]{--smartbanner-scale:1;position:fixed;top:0;left:0;overflow-x:hidden;width:1026px;height:290px;z-index:1337;display:flex;flex-direction:row;align-items:center;align-content:center;transform-origin:bottom left;transition:transform .1s linear 0s;-webkit-backface-visibility:hidden;backface-visibility:hidden;background-color:#fff;background-image:url(\"data:image/svg+xml,%3Csvg width='343' height='26' viewBox='0 0 343 26' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0)'%3E%3Cpath d='M188.526 -54.6622L240.865 -66.9132L255.951 6.83604L158.717 14.3087L205.066 -31.022L188.526 -54.6622Z' fill='%23E3FF09'/%3E%3Cpath d='M158.717 14.3085L87.1183 6.33803L118.168 -39.3481L118.168 -94.4787L156.563 -37.9022L158.717 14.3085Z' fill='%236BFF36'/%3E%3Cpath d='M307.744 -42.4788L261.507 -102.596L240.865 -66.913L255.95 6.83614L328.121 26.0978L307.744 -42.4788Z' fill='%236BFF36'/%3E%3Cpath d='M118.168 -94.4788L181.365 -82.8398L188.526 -54.6625L205.066 -31.0223L158.717 14.3084L156.563 -37.9023L118.168 -94.4788Z' fill='%23FFFF09'/%3E%3Cpath d='M87.1181 6.33758L24.7298 26.0971L1.58595 -45.2068L66.0459 -34.2303L35.586 -81.003L80.9834 -102.596L118.169 -39.3486L87.1181 6.33758Z' fill='%23FFFF09'/%3E%3Cpath d='M24.7296 26.0974L-48.3615 -39.3482L1.58673 -111.271L35.5858 -81.0026L66.0466 -34.2299L1.58673 -45.2064L24.7296 26.0974Z' fill='%23E3FF09'/%3E%3Cpath d='M380.627 -38.2493L380.627 -7.39033L360.073 -22.2397L328.121 26.0971L307.744 -42.4786L372.686 -105.74L348.758 -52.5816L388.524 -66.9138L380.627 -38.2493Z' fill='%23FFFF09'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0'%3E%3Crect width='1038' height='711' transform='matrix(-4.37114e-08 -1 -1 4.37114e-08 517 26)' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E%0A\");box-shadow:0 1px 2px rgba(0,0,0,.13),0 1px 4px rgba(0,0,0,.08),0 1px 15px rgba(0,0,0,.17);padding-top:75px;padding-bottom:25px}.smartbanner[_ngcontent-%COMP%], .smartbanner__icon[_ngcontent-%COMP%]{background-repeat:no-repeat;background-size:contain}.smartbanner__icon[_ngcontent-%COMP%]{height:200px;width:200px;border-radius:5%;background-image:url(\"data:image/svg+xml,%3Csvg width='68' height='68' viewBox='0 0 68 68' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg filter='url(%23filter0_d)'%3E%3Crect x='4' y='3' width='60' height='60' rx='3' fill='white'/%3E%3Crect x='4' y='3' width='60' height='60' fill='%23FFFF09'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M15 33.0002C15 43.4935 23.5068 52 34 52C44.4932 52 53 43.4935 53 33.0002C53 22.5069 44.4932 14 34 14C23.5068 14 15 22.5069 15 33.0002Z' fill='black'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M20.4217 40.2045C20.9011 41.0947 21.4642 41.9334 22.1012 42.7087L44.0447 20.765C41.5274 18.698 38.3511 17.4059 34.8789 17.2478L20.4217 40.2045ZM24.335 44.9292C25.1165 45.5645 25.96 46.1245 26.8563 46.6L49.7362 32.1911C49.5975 28.7127 48.3204 25.528 46.2657 22.9987L24.335 44.9292ZM30.5555 47.9927C40.5015 50.2058 47.9922 43.1715 49.3979 36.1267L30.5555 47.9927Z' fill='white'/%3E%3C/g%3E%3Cdefs%3E%3Cfilter id='filter0_d' x='0' y='0' width='68' height='68' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'%3E%3CfeFlood flood-opacity='0' result='BackgroundImageFix'/%3E%3CfeColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'/%3E%3CfeOffset dy='1'/%3E%3CfeGaussianBlur stdDeviation='2'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0'/%3E%3CfeBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow'/%3E%3CfeBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow' result='shape'/%3E%3C/filter%3E%3C/defs%3E%3C/svg%3E\")}.smartbanner__info[_ngcontent-%COMP%]{color:#000;margin-left:2em}.smartbanner__info__title[_ngcontent-%COMP%]{font-size:4em;font-weight:700}.smartbanner__info__sub[_ngcontent-%COMP%]{font-size:3em}.smartbanner__button[_ngcontent-%COMP%]{margin-left:auto;margin-right:1em;font-size:3em;max-width:240px}.smartbanner__close[_ngcontent-%COMP%]{margin:0 2em;height:80px;width:80px;background-size:contain;background-repeat:no-repeat;background-image:url(\"data:image/svg+xml, %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath d='M15.898,4.045c-0.271-0.272-0.713-0.272-0.986,0l-4.71,4.711L5.493,4.045c-0.272-0.272-0.714-0.272-0.986,0s-0.272,0.714,0,0.986l4.709,4.711l-4.71,4.711c-0.272,0.271-0.272,0.713,0,0.986c0.136,0.136,0.314,0.203,0.492,0.203c0.179,0,0.357-0.067,0.493-0.203l4.711-4.711l4.71,4.711c0.137,0.136,0.314,0.203,0.494,0.203c0.178,0,0.355-0.067,0.492-0.203c0.273-0.273,0.273-0.715,0-0.986l-4.711-4.711l4.711-4.711C16.172,4.759,16.172,4.317,15.898,4.045z'%3E%3C/path%3E%3C/svg%3E\")}"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AppBannerComponent, [{
        type: Component,
        args: [{
                selector: "lib-app-banner",
                templateUrl: "./app-banner.component.html",
                styleUrls: ["./app-banner.component.scss"],
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return []; }, { appBannerRef: [{
            type: ViewChild,
            args: ["AppBanner", { static: false }]
        }] }); })();

function PrivacyPolicyPopupComponent_div_0_Template(rf, ctx) { if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵelementStart(1, "svg-icon", 2);
    i0.ɵɵlistener("click", function PrivacyPolicyPopupComponent_div_0_Template_svg_icon_click_1_listener() { i0.ɵɵrestoreView(_r2); const ctx_r1 = i0.ɵɵnextContext(); return ctx_r1.removePopup(); });
    i0.ɵɵelementEnd();
    i0.ɵɵtext(2);
    i0.ɵɵpipe(3, "translate");
    i0.ɵɵelementStart(4, "a", 3);
    i0.ɵɵtext(5);
    i0.ɵɵpipe(6, "translate");
    i0.ɵɵelementEnd();
    i0.ɵɵtext(7);
    i0.ɵɵpipe(8, "translate");
    i0.ɵɵelementStart(9, "a", 4);
    i0.ɵɵtext(10, "privacy@eslgaming.com");
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵstyleProp("width", 20, "px")("height", 20, "px");
    i0.ɵɵproperty("icon", "close");
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(3, 8, "privacy-policy-popup:link"), " ");
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(6, 10, "privacy-policy-popup:here"), " ");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(". ", i0.ɵɵpipeBind1(8, 12, "privacy-policy-popup:email"), " ");
} }
class PrivacyPolicyPopupComponent {
    constructor() {
        this.showPopup = false;
        this.localStorageKey = "showPlayPrivacyPopup";
    }
    ngOnInit() {
        const localStorageEntry = localStorage.getItem(this.localStorageKey);
        if (localStorageEntry === null) {
            localStorage.setItem(this.localStorageKey, "1");
            this.showPopup = true;
        }
        else {
            this.showPopup = !!parseInt(localStorageEntry);
        }
    }
    removePopup() {
        localStorage.setItem(this.localStorageKey, "0");
        this.showPopup = false;
    }
}
PrivacyPolicyPopupComponent.ɵfac = function PrivacyPolicyPopupComponent_Factory(t) { return new (t || PrivacyPolicyPopupComponent)(); };
PrivacyPolicyPopupComponent.ɵcmp = i0.ɵɵdefineComponent({ type: PrivacyPolicyPopupComponent, selectors: [["privacy-policy-popup"]], decls: 1, vars: 1, consts: [["class", "privacy-policy-popup", 4, "ngIf"], [1, "privacy-policy-popup"], [1, "privacy-policy-popup__close", 3, "icon", "click"], ["href", "https://www.eslgaming.com/privacypolicy", "target", "_blank", "rel", "noopener"], ["href", "mailto:privacy@eslgaming.com"]], template: function PrivacyPolicyPopupComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, PrivacyPolicyPopupComponent_div_0_Template, 11, 14, "div", 0);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.showPopup);
    } }, directives: [i1$1.NgIf, SvgIconComponent], pipes: [i1.TranslatePipe], styles: [".privacy-policy-popup[_ngcontent-%COMP%]{position:fixed;bottom:2rem;left:50%;transform:translateX(-50%);padding:1rem 2.5rem 1rem 1rem;border-radius:3px;background:var(--midnight-black);color:var(--frozen-white);font-size:1rem;box-shadow:0 3px 6px rgba(0,0,0,.16),0 3px 6px rgba(0,0,0,.23);z-index:9999}.privacy-policy-popup[_ngcontent-%COMP%]   a[_ngcontent-%COMP%]{color:var(--psycho-yellow)}.privacy-policy-popup__close[_ngcontent-%COMP%]{position:absolute;top:.5rem;right:.5rem;color:var(--frozen-white);cursor:pointer}.privacy-policy-popup__close[_ngcontent-%COMP%]:hover{color:var(--midnight-grey4)}.privacy-policy-popup__close[_ngcontent-%COMP%]:active{color:var(--midnight-grey3)}"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PrivacyPolicyPopupComponent, [{
        type: Component,
        args: [{
                selector: "privacy-policy-popup",
                templateUrl: "./privacy-policy-popup.component.html",
                styleUrls: ["./privacy-policy-popup.component.scss"],
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return []; }, null); })();

class CommonsComponentModule {
    constructor(iconRepo) {
        const icons = [
            { identifier: "twitter", icon: twitter },
            { identifier: "facebook", icon: facebook },
            { identifier: "chevron-right", icon: chevronRight },
            { identifier: "chevron-left", icon: chevronLeft },
            { identifier: "group", icon: group },
            { identifier: "link", icon: link },
            { identifier: "file", icon: file },
            { identifier: "info", icon: info },
            { identifier: "replacement", icon: replacement },
            { identifier: "esl-community", icon: labelEslCommunity },
            { identifier: "esl-major", icon: labelEslMajor },
            { identifier: "esl-pro", icon: labelEslPro },
            { identifier: "esl-pro_qualifier", icon: labelEslPro },
            { identifier: "esl-open", icon: labelEslOpen },
            { identifier: "league-date", icon: leagueDate },
            { identifier: "league-bracket", icon: leagueBracket },
            { identifier: "league-signup", icon: leagueSignup },
            { identifier: "league-time", icon: leagueTime },
            { identifier: "league-wait", icon: leagueWait },
            { identifier: "league-game-id", icon: replacement },
            { identifier: "league-game", icon: replacement },
            { identifier: "league-league-info", icon: leagueLeagueInfo },
            { identifier: "league-match", icon: leagueMatch },
            { identifier: "league-mode", icon: replacement },
            { identifier: "league-participants", icon: leagueParticipants },
            { identifier: "league-requirements", icon: leagueRequirements },
            { identifier: "league-rules", icon: leagueRules },
            { identifier: "league-series", icon: leagueSeries },
            { identifier: "league-prizepool", icon: leaguePrizepool },
            { identifier: "premium", icon: premiumIcon },
            { identifier: "trusted", icon: trustedIcon },
            { identifier: "settings", icon: settingsIcon },
            { identifier: "close", icon: close },
        ];
        icons.forEach(iconRepo.addIcon.bind(iconRepo));
    }
}
CommonsComponentModule.ɵfac = function CommonsComponentModule_Factory(t) { return new (t || CommonsComponentModule)(i0.ɵɵinject(SvgIconsRepository)); };
CommonsComponentModule.ɵmod = i0.ɵɵdefineNgModule({ type: CommonsComponentModule });
CommonsComponentModule.ɵinj = i0.ɵɵdefineInjector({ providers: [{ provide: DOCUMENT, useFactory: getDocument }], imports: [[CommonModule, CommonsDateModule, CommonsLoadingModule, TranslationModule, SvgIconsModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(CommonsComponentModule, { declarations: [CountryFlagComponent,
        ButtonComponent,
        TooltipComponent,
        SquareImageComponent,
        ExpansionPanelComponent,
        PremiumIconComponent,
        TrustedIconComponent,
        LabeledValueComponent,
        StepperComponent,
        StepComponent,
        StepTitleComponent,
        ProgressBarComponent,
        FileUploadFileComponent,
        FileUploadDropzoneComponent,
        SvgIconComponent,
        CopyToClipboardDirective,
        AutofocusDirective,
        TooltipDirective,
        GameIconComponent,
        SkillIconComponent,
        EmptyCallActionComponent,
        PlayHeaderComponent,
        FormatDateComponent,
        TrustedBadgeComponent,
        AppBannerComponent,
        PrivacyPolicyPopupComponent,
        InheritClassComponent], imports: [CommonModule, CommonsDateModule, CommonsLoadingModule, TranslationModule, SvgIconsModule], exports: [CountryFlagComponent,
        ButtonComponent,
        TooltipComponent,
        SquareImageComponent,
        ExpansionPanelComponent,
        PremiumIconComponent,
        TrustedIconComponent,
        LabeledValueComponent,
        StepperComponent,
        StepComponent,
        FileUploadFileComponent,
        FileUploadDropzoneComponent,
        ProgressBarComponent,
        SvgIconComponent,
        CopyToClipboardDirective,
        AutofocusDirective,
        TooltipDirective,
        GameIconComponent,
        SkillIconComponent,
        EmptyCallActionComponent,
        PlayHeaderComponent,
        FormatDateComponent,
        TrustedBadgeComponent,
        AppBannerComponent,
        PrivacyPolicyPopupComponent,
        InheritClassComponent] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CommonsComponentModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, CommonsDateModule, CommonsLoadingModule, TranslationModule, SvgIconsModule],
                declarations: [
                    CountryFlagComponent,
                    ButtonComponent,
                    TooltipComponent,
                    SquareImageComponent,
                    ExpansionPanelComponent,
                    PremiumIconComponent,
                    TrustedIconComponent,
                    LabeledValueComponent,
                    StepperComponent,
                    StepComponent,
                    StepTitleComponent,
                    ProgressBarComponent,
                    FileUploadFileComponent,
                    FileUploadDropzoneComponent,
                    SvgIconComponent,
                    CopyToClipboardDirective,
                    AutofocusDirective,
                    TooltipDirective,
                    GameIconComponent,
                    SkillIconComponent,
                    EmptyCallActionComponent,
                    PlayHeaderComponent,
                    FormatDateComponent,
                    TrustedBadgeComponent,
                    AppBannerComponent,
                    PrivacyPolicyPopupComponent,
                    InheritClassComponent,
                ],
                providers: [{ provide: DOCUMENT, useFactory: getDocument }],
                exports: [
                    CountryFlagComponent,
                    ButtonComponent,
                    TooltipComponent,
                    SquareImageComponent,
                    ExpansionPanelComponent,
                    PremiumIconComponent,
                    TrustedIconComponent,
                    LabeledValueComponent,
                    StepperComponent,
                    StepComponent,
                    FileUploadFileComponent,
                    FileUploadDropzoneComponent,
                    ProgressBarComponent,
                    SvgIconComponent,
                    CopyToClipboardDirective,
                    AutofocusDirective,
                    TooltipDirective,
                    GameIconComponent,
                    SkillIconComponent,
                    EmptyCallActionComponent,
                    PlayHeaderComponent,
                    FormatDateComponent,
                    TrustedBadgeComponent,
                    AppBannerComponent,
                    PrivacyPolicyPopupComponent,
                    InheritClassComponent,
                ],
                entryComponents: [TooltipComponent, AppBannerComponent],
            }]
    }], function () { return [{ type: SvgIconsRepository }]; }, null); })();

class CommonsErrorModule {
}
CommonsErrorModule.ɵfac = function CommonsErrorModule_Factory(t) { return new (t || CommonsErrorModule)(); };
CommonsErrorModule.ɵmod = i0.ɵɵdefineNgModule({ type: CommonsErrorModule });
CommonsErrorModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, CommonsComponentModule, TranslationModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(CommonsErrorModule, { declarations: [InlineErrorComponent], imports: [CommonModule, CommonsComponentModule, TranslationModule], exports: [InlineErrorComponent] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CommonsErrorModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, CommonsComponentModule, TranslationModule],
                declarations: [InlineErrorComponent],
                exports: [InlineErrorComponent],
            }]
    }], null, null); })();

const _c0 = [[["", "input-label", ""]], [["", "input-description", ""]]];
const _c1 = function (a0) { return { "is-disabled": a0 }; };
const _c2 = ["[input-label]", "[input-description]"];
class DescribedCheckboxComponent {
    constructor() {
        this.isDisabled = false;
        this.onChange = new EventEmitter();
    }
    // Hack because of weird behaviour with custom elements, labels and nested spans inside
    // Please do not remove [ngModel] stuff from the HTML
    onClick(e) {
        if (this.isDisabled || (e.target && e.target.type === "checkbox")) {
            return;
        }
        e.preventDefault();
        this.value = !this.value;
        this.onChange.emit(this.value);
    }
}
DescribedCheckboxComponent.ɵfac = function DescribedCheckboxComponent_Factory(t) { return new (t || DescribedCheckboxComponent)(); };
DescribedCheckboxComponent.ɵcmp = i0.ɵɵdefineComponent({ type: DescribedCheckboxComponent, selectors: [["described-checkbox"]], hostBindings: function DescribedCheckboxComponent_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("click", function DescribedCheckboxComponent_click_HostBindingHandler($event) { return ctx.onClick($event); });
    } }, inputs: { isDisabled: ["disabled", "isDisabled"], value: ["checked", "value"] }, outputs: { onChange: "checkedChange" }, ngContentSelectors: _c2, decls: 7, vars: 6, consts: [[1, "c-described-checkbox", "o-media", 3, "ngClass"], ["type", "checkbox", 1, "c-described-checkbox__checkbox", "o-media__figure", 3, "ngModel", "checked", "disabled", "ngModelChange"], [1, "o-media__body"], [1, "c-described-checkbox__title"], [1, "c-described-checkbox__description"]], template: function DescribedCheckboxComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef(_c0);
        i0.ɵɵelementStart(0, "label", 0);
        i0.ɵɵelementStart(1, "input", 1);
        i0.ɵɵlistener("ngModelChange", function DescribedCheckboxComponent_Template_input_ngModelChange_1_listener($event) { return ctx.onChange.emit($event); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(2, "span", 2);
        i0.ɵɵelementStart(3, "span", 3);
        i0.ɵɵprojection(4);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(5, "span", 4);
        i0.ɵɵprojection(6, 1);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(4, _c1, ctx.isDisabled));
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngModel", ctx.value)("checked", ctx.value)("disabled", ctx.isDisabled);
    } }, directives: [i1$1.NgClass, i2$1.CheckboxControlValueAccessor, i2$1.NgControlStatus, i2$1.NgModel], styles: ["[_nghost-%COMP%] {\n                display: block;\n                width: 100%;\n            }"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DescribedCheckboxComponent, [{
        type: Component,
        args: [{
                selector: "described-checkbox",
                templateUrl: "./described-checkbox.component.html",
                styles: [
                    `
            :host {
                display: block;
                width: 100%;
            }
        `,
                ],
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { isDisabled: [{
            type: Input,
            args: ["disabled"]
        }], value: [{
            type: Input,
            args: ["checked"]
        }], onChange: [{
            type: Output,
            args: ["checkedChange"]
        }], onClick: [{
            type: HostListener,
            args: ["click", ["$event"]]
        }] }); })();

class CommonsFormModule {
}
CommonsFormModule.ɵfac = function CommonsFormModule_Factory(t) { return new (t || CommonsFormModule)(); };
CommonsFormModule.ɵmod = i0.ɵɵdefineNgModule({ type: CommonsFormModule });
CommonsFormModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[FormsModule, CommonModule, CommonsComponentModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(CommonsFormModule, { declarations: [DescribedCheckboxComponent], imports: [FormsModule, CommonModule, CommonsComponentModule], exports: [DescribedCheckboxComponent] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CommonsFormModule, [{
        type: NgModule,
        args: [{
                imports: [FormsModule, CommonModule, CommonsComponentModule],
                exports: [DescribedCheckboxComponent],
                declarations: [DescribedCheckboxComponent],
            }]
    }], null, null); })();

"use strict";
// Countries according to the ISO-3166-1 alpha-2 standard.
const countries = {
    AF: "Afghanistan",
    AX: "Aland Islands",
    AL: "Albania",
    DZ: "Algeria",
    AS: "American Samoa",
    AD: "Andorra",
    AO: "Angola",
    AI: "Anguilla",
    AQ: "Antarctica",
    AG: "Antigua And Barbuda",
    AR: "Argentina",
    AM: "Armenia",
    AW: "Aruba",
    AU: "Australia",
    AT: "Austria",
    AZ: "Azerbaijan",
    BS: "Bahamas",
    BH: "Bahrain",
    BD: "Bangladesh",
    BB: "Barbados",
    BY: "Belarus",
    BE: "Belgium",
    BZ: "Belize",
    BJ: "Benin",
    BM: "Bermuda",
    BT: "Bhutan",
    BO: "Bolivia",
    BQ: "Bonaire, Sint Eustatius, Saba",
    BA: "Bosnia And Herzegovina",
    BW: "Botswana",
    BV: "Bouvet Island",
    BR: "Brazil",
    IO: "British Indian Ocean Territory",
    BN: "Brunei Darussalam",
    BG: "Bulgaria",
    BF: "Burkina Faso",
    BI: "Burundi",
    KH: "Cambodia",
    CM: "Cameroon",
    CA: "Canada",
    CV: "Cape Verde",
    KY: "Cayman Islands",
    CF: "Central African Republic",
    TD: "Chad",
    CL: "Chile",
    CN: "China",
    CX: "Christmas Island",
    CC: "Cocos (Keeling) Islands",
    CO: "Colombia",
    KM: "Comoros",
    CG: "Congo",
    CD: "Congo (Democratic Republic)",
    CK: "Cook Islands",
    CR: "Costa Rica",
    CI: 'Cote D"Ivoire',
    HR: "Croatia",
    CU: "Cuba",
    CW: "Curaçao",
    CY: "Cyprus",
    CZ: "Czech Republic",
    DK: "Denmark",
    DJ: "Djibouti",
    DM: "Dominica",
    DO: "Dominican Republic",
    EC: "Ecuador",
    EG: "Egypt",
    SV: "El Salvador",
    GQ: "Equatorial Guinea",
    ER: "Eritrea",
    EE: "Estonia",
    ET: "Ethiopia",
    FK: "Falkland Islands (Malvinas)",
    FO: "Faroe Islands",
    FJ: "Fiji",
    FI: "Finland",
    FR: "France",
    GF: "French Guiana",
    PF: "French Polynesia",
    TF: "French Southern Territories",
    GA: "Gabon",
    GM: "Gambia",
    GE: "Georgia",
    DE: "Germany",
    GH: "Ghana",
    GI: "Gibraltar",
    GR: "Greece",
    GL: "Greenland",
    GD: "Grenada",
    GP: "Guadeloupe",
    GU: "Guam",
    GT: "Guatemala",
    GG: "Guernsey",
    GN: "Guinea",
    GW: "Guinea-Bissau",
    GY: "Guyana",
    HT: "Haiti",
    HM: "Heard Island & Mcdonald Islands",
    VA: "Holy See (Vatican City State)",
    HN: "Honduras",
    HK: "Hong Kong",
    HU: "Hungary",
    IS: "Iceland",
    IN: "India",
    ID: "Indonesia",
    IR: "Iran (Islamic Republic Of)",
    IQ: "Iraq",
    IE: "Ireland",
    IM: "Isle Of Man",
    IL: "Israel",
    IT: "Italy",
    JM: "Jamaica",
    JP: "Japan",
    JE: "Jersey",
    JO: "Jordan",
    KZ: "Kazakhstan",
    KE: "Kenya",
    KI: "Kiribati",
    KP: "Korea (Democratic People's Republic)",
    KR: "Korea",
    XK: "Kosovo",
    KW: "Kuwait",
    KG: "Kyrgyzstan",
    LA: "Lao People's Democratic Republic",
    LV: "Latvia",
    LB: "Lebanon",
    LS: "Lesotho",
    LR: "Liberia",
    LY: "Libyan Arab Jamahiriya",
    LI: "Liechtenstein",
    LT: "Lithuania",
    LU: "Luxembourg",
    MO: "Macao",
    MK: "North Macedonia",
    MG: "Madagascar",
    MW: "Malawi",
    MY: "Malaysia",
    MV: "Maldives",
    ML: "Mali",
    MT: "Malta",
    MH: "Marshall Islands",
    MQ: "Martinique",
    MR: "Mauritania",
    MU: "Mauritius",
    YT: "Mayotte",
    MX: "Mexico",
    FM: "Micronesia (Federated States Of)",
    MD: "Moldova",
    MC: "Monaco",
    MN: "Mongolia",
    ME: "Montenegro",
    MS: "Montserrat",
    MA: "Morocco",
    MZ: "Mozambique",
    MM: "Myanmar",
    NA: "Namibia",
    NR: "Nauru",
    NP: "Nepal",
    NL: "Netherlands",
    AN: "Netherlands Antilles",
    NC: "New Caledonia",
    NZ: "New Zealand",
    NI: "Nicaragua",
    NE: "Niger",
    NG: "Nigeria",
    NU: "Niue",
    NF: "Norfolk Island",
    MP: "Northern Mariana Islands",
    NO: "Norway",
    OM: "Oman",
    PK: "Pakistan",
    PW: "Palau",
    PS: "Palestinian Territory (Occupied)",
    PA: "Panama",
    PG: "Papua New Guinea",
    PY: "Paraguay",
    PE: "Peru",
    PH: "Philippines",
    PN: "Pitcairn",
    PL: "Poland",
    PT: "Portugal",
    PR: "Puerto Rico",
    QA: "Qatar",
    RE: "Reunion",
    RO: "Romania",
    RU: "Russian Federation",
    RW: "Rwanda",
    BL: "Saint Barthelemy",
    SH: "Saint Helena",
    KN: "Saint Kitts And Nevis",
    LC: "Saint Lucia",
    MF: "Saint Martin",
    PM: "Saint Pierre And Miquelon",
    VC: "Saint Vincent And Grenadines",
    WS: "Samoa",
    SM: "San Marino",
    ST: "Sao Tome And Principe",
    SA: "Saudi Arabia",
    SN: "Senegal",
    RS: "Serbia",
    SC: "Seychelles",
    SL: "Sierra Leone",
    SG: "Singapore",
    SK: "Slovakia",
    SI: "Slovenia",
    SB: "Solomon Islands",
    SO: "Somalia",
    ZA: "South Africa",
    GS: "South Georgia And Sandwich Isl.",
    SS: "South Sudan",
    ES: "Spain",
    LK: "Sri Lanka",
    SD: "Sudan",
    SR: "Suriname",
    SJ: "Svalbard And Jan Mayen",
    SZ: "Swaziland",
    SE: "Sweden",
    CH: "Switzerland",
    SY: "Syrian Arab Republic",
    TW: "Taiwan",
    TJ: "Tajikistan",
    TZ: "Tanzania",
    TH: "Thailand",
    TL: "Timor-Leste",
    TG: "Togo",
    TK: "Tokelau",
    TO: "Tonga",
    TT: "Trinidad And Tobago",
    TN: "Tunisia",
    TR: "Türkiye",
    TM: "Turkmenistan",
    TC: "Turks And Caicos Islands",
    TV: "Tuvalu",
    UG: "Uganda",
    UA: "Ukraine",
    AE: "United Arab Emirates",
    GB: "United Kingdom",
    US: "United States",
    UM: "United States Outlying Islands",
    UY: "Uruguay",
    UZ: "Uzbekistan",
    VU: "Vanuatu",
    VE: "Venezuela",
    VN: "Vietnam",
    VG: "Virgin Islands (British)",
    VI: "Virgin Islands (U.S.)",
    WF: "Wallis And Futuna",
    EH: "Western Sahara",
    YE: "Yemen",
    ZM: "Zambia",
    ZW: "Zimbabwe",
};
// Custom ESL Play "countries"/regions.
const playRegions = {
    ASIA: "Asia",
    EU: "Europe",
    BENELUX: "Benelux",
    global: "Global",
    null: "International",
};

const demonyms = {
    AW: "Aruban",
    AF: "Afghan",
    AO: "Angolan",
    AI: "Anguillian",
    AX: "\u00c5landish",
    AL: "Albanian",
    AD: "Andorran",
    AE: "Emirati",
    AR: "Argentine",
    AM: "Armenian",
    AS: "American Samoan",
    AQ: "Antarctican",
    TF: "French",
    AG: "Antiguan, Barbudan",
    AU: "Australian",
    AT: "Austrian",
    AZ: "Azerbaijani",
    BI: "Burundian",
    BE: "Belgian",
    BJ: "Beninese",
    BF: "Burkinabe",
    BD: "Bangladeshi",
    BG: "Bulgarian",
    BH: "Bahraini",
    BS: "Bahamian",
    BA: "Bosnian, Herzegovinian",
    BL: "Saint Barth\u00e9lemy Islander",
    SH: "Saint Helenian",
    BY: "Belarusian",
    BZ: "Belizean",
    BM: "Bermudian",
    BO: "Bolivian",
    BQ: "Dutch",
    BR: "Brazilian",
    BB: "Barbadian",
    BN: "Bruneian",
    BT: "Bhutanese",
    BW: "Motswana",
    CF: "Central African",
    CA: "Canadian",
    CC: "Cocos Islander",
    CH: "Swiss",
    CL: "Chilean",
    CN: "Chinese",
    CI: "Ivorian",
    CM: "Cameroonian",
    CD: "Congolese",
    CG: "Congolese",
    CK: "Cook Islander",
    CO: "Colombian",
    KM: "Comoran",
    CV: "Cape Verdian",
    CR: "Costa Rican",
    CU: "Cuban",
    CW: "Dutch",
    CX: "Christmas Islander",
    KY: "Caymanian",
    CY: "Cypriot",
    CZ: "Czech",
    DE: "German",
    DJ: "Djibouti",
    DM: "Dominican",
    DK: "Danish",
    DO: "Dominican",
    DZ: "Algerian",
    EC: "Ecuadorean",
    EG: "Egyptian",
    ER: "Eritrean",
    EH: "Sahrawi",
    ES: "Spanish",
    EE: "Estonian",
    ET: "Ethiopian",
    FI: "Finnish",
    FJ: "Fijian",
    FK: "Falkland Islander",
    FR: "French",
    FO: "Faroese",
    FM: "Micronesian",
    GA: "Gabonese",
    GB: "British",
    GE: "Georgian",
    GG: "Channel Islander",
    GH: "Ghanaian",
    GI: "Gibraltar",
    GN: "Guinean",
    GP: "Guadeloupian",
    GM: "Gambian",
    GW: "Guinea-Bissauan",
    GQ: "Equatorial Guinean",
    GR: "Greek",
    GD: "Grenadian",
    GL: "Greenlandic",
    GT: "Guatemalan",
    GF: "Guianan",
    GU: "Guamanian",
    GY: "Guyanese",
    HK: "Hong Konger",
    HM: "Heard and McDonald Islander",
    HN: "Honduran",
    HR: "Croatian",
    HT: "Haitian",
    HU: "Hungarian",
    ID: "Indonesian",
    IM: "Manx",
    IN: "Indian",
    IO: "Indian",
    IE: "Irish",
    IR: "Iranian",
    IQ: "Iraqi",
    IS: "Icelander",
    IL: "Israeli",
    IT: "Italian",
    JM: "Jamaican",
    JE: "Channel Islander",
    JO: "Jordanian",
    JP: "Japanese",
    KZ: "Kazakhstani",
    KE: "Kenyan",
    KG: "Kirghiz",
    KH: "Cambodian",
    KI: "I-Kiribati",
    KN: "Kittitian or Nevisian",
    KR: "South Korean",
    XK: "Kosovar",
    KW: "Kuwaiti",
    LA: "Laotian",
    LB: "Lebanese",
    LR: "Liberian",
    LY: "Libyan",
    LC: "Saint Lucian",
    LI: "Liechtensteiner",
    LK: "Sri Lankan",
    LS: "Mosotho",
    LT: "Lithuanian",
    LU: "Luxembourger",
    LV: "Latvian",
    MO: "Macanese",
    MF: "Saint Martin Islander",
    MA: "Moroccan",
    MC: "Monegasque",
    MD: "Moldovan",
    MG: "Malagasy",
    MV: "Maldivan",
    MX: "Mexican",
    MH: "Marshallese",
    MK: "Macedonian",
    ML: "Malian",
    MT: "Maltese",
    MM: "Bamar",
    ME: "Montenegrin",
    MN: "Mongolian",
    MP: "American",
    MZ: "Mozambican",
    MR: "Mauritanian",
    MS: "Montserratian",
    MQ: "French",
    MU: "Mauritian",
    MW: "Malawian",
    MY: "Malaysian",
    YT: "Mahoran",
    NA: "Namibian",
    NC: "New Caledonian",
    NE: "Nigerien",
    NF: "Norfolk Islander",
    NG: "Nigerian",
    NI: "Nicaraguan",
    NU: "Niuean",
    NL: "Dutch",
    NO: "Norwegian",
    NP: "Nepalese",
    NR: "Nauruan",
    NZ: "New Zealander",
    OM: "Omani",
    PK: "Pakistani",
    PA: "Panamanian",
    PN: "Pitcairn Islander",
    PE: "Peruvian",
    PH: "Filipino",
    PW: "Palauan",
    PG: "Papua New Guinean",
    PL: "Polish",
    PR: "Puerto Rican",
    KP: "North Korean",
    PT: "Portuguese",
    PY: "Paraguayan",
    PS: "Palestinian",
    PF: "French Polynesian",
    QA: "Qatari",
    RE: "French",
    RO: "Romanian",
    RU: "Russian",
    RW: "Rwandan",
    SA: "Saudi Arabian",
    SD: "Sudanese",
    SN: "Senegalese",
    SG: "Singaporean",
    GS: "South Georgian South Sandwich Islander",
    SJ: "Norwegian",
    SB: "Solomon Islander",
    SL: "Sierra Leonean",
    SV: "Salvadoran",
    SM: "Sammarinese",
    SO: "Somali",
    PM: "French",
    RS: "Serbian",
    SS: "South Sudanese",
    ST: "Sao Tomean",
    SR: "Surinamer",
    SK: "Slovak",
    SI: "Slovene",
    SE: "Swedish",
    SZ: "Swazi",
    SX: "St. Maartener",
    SC: "Seychellois",
    SY: "Syrian",
    TC: "Turks and Caicos Islander",
    TD: "Chadian",
    TG: "Togolese",
    TH: "Thai",
    TJ: "Tadzhik",
    TK: "Tokelauan",
    TM: "Turkmen",
    TL: "East Timorese",
    TO: "Tongan",
    TT: "Trinidadian",
    TN: "Tunisian",
    TR: "Turkish",
    TV: "Tuvaluan",
    TW: "Taiwanese",
    TZ: "Tanzanian",
    UG: "Ugandan",
    UA: "Ukrainian",
    UK: "British",
    UM: "American",
    UY: "Uruguayan",
    US: "American",
    UZ: "Uzbekistani",
    VA: "Vatican",
    VC: "Saint Vincentian",
    VE: "Venezuelan",
    VG: "Virgin Islander",
    VI: "Virgin Islander",
    VN: "Vietnamese",
    VU: "Ni-Vanuatu",
    WF: "Wallis and Futuna Islander",
    WS: "Samoan",
    YE: "Yemeni",
    ZA: "South African",
    ZM: "Zambian",
    ZW: "Zimbabwean",
};

class PlayCountryNames {
    constructor() { }
    getCountry(countryCode) {
        if (!countryCode) {
            return null;
        }
        countryCode = countryCode.toUpperCase();
        if (countries.hasOwnProperty(countryCode)) {
            return countries[countryCode];
        }
        else {
            return countryCode;
        }
    }
    getDemonym(countryCode) {
        if (!countryCode) {
            return null;
        }
        countryCode = countryCode.toUpperCase();
        if (demonyms.hasOwnProperty(countryCode)) {
            return demonyms[countryCode];
        }
        else {
            return countryCode;
        }
    }
    getPlayRegion(countryCode) {
        if (!countryCode) {
            return null;
        }
        countryCode = countryCode.toUpperCase();
        if (playRegions.hasOwnProperty(countryCode)) {
            return playRegions[countryCode];
        }
        else {
            return countryCode;
        }
    }
    getCountries() {
        return countries;
    }
    getDemonyms() {
        return demonyms;
    }
    getPlayRegions() {
        return playRegions;
    }
}
PlayCountryNames.ɵfac = function PlayCountryNames_Factory(t) { return new (t || PlayCountryNames)(); };
PlayCountryNames.ɵprov = i0.ɵɵdefineInjectable({ token: PlayCountryNames, factory: PlayCountryNames.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PlayCountryNames, [{
        type: Injectable
    }], function () { return []; }, null); })();

class CommonsCountryNameModule {
}
CommonsCountryNameModule.ɵfac = function CommonsCountryNameModule_Factory(t) { return new (t || CommonsCountryNameModule)(); };
CommonsCountryNameModule.ɵmod = i0.ɵɵdefineNgModule({ type: CommonsCountryNameModule });
CommonsCountryNameModule.ɵinj = i0.ɵɵdefineInjector({ providers: [PlayCountryNames], imports: [[]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CommonsCountryNameModule, [{
        type: NgModule,
        args: [{
                imports: [],
                declarations: [],
                providers: [PlayCountryNames],
                exports: [],
            }]
    }], null, null); })();

class RestrictionFilterService {
    constructor() {
        this.restrictionExceptions = [
            "playerLeagueBanned",
            "teamLeagueBanned",
            "alternateRoster",
            "playerPenaltyPoints",
            "teamPenaltyPoints",
        ];
        this.teamRestrictions = ["whitelist", "alternateLeague", "inCountryList", "residence_nationality", "teamSize", "logo"];
        this.memberRestrictions = ["gameaccount"];
        this.playerRestrictions = ["gameaccount"];
    }
    getTeamRestrictions(restrictions) {
        return filter(restrictions, (restriction) => {
            return this.restrictionExceptions.indexOf(restriction.type) === -1 && this.affectsTeam(restriction);
        });
    }
    /**
     * Checks if the team restriction has no minmember attribute,
     * if it has one it must be not null/0
     *
     * @param restriction
     */
    affectsTeam(restriction) {
        return (this.teamRestrictions.indexOf(restriction.type) !== -1 &&
            (!restriction.params ||
                isUndefined(restriction.params.minmembers) ||
                restriction.type === "teamSize" ||
                (restriction.params &&
                    !isUndefined(restriction.params.minmembers) &&
                    restriction.params.minmembers !== null &&
                    restriction.params.minmembers !== 0)));
    }
    /**
     * Gets all the restrictions that affects a team member (doesn't equal player)
     * Get all restrictions that don't have a minmembers attribute, have a passedMembers attribute
     *
     * @param restrictions
     */
    getMemberRestrictions(restrictions) {
        return filter(restrictions, (restriction) => {
            return (this.restrictionExceptions.indexOf(restriction.type) === -1 &&
                (this.memberRestrictions.indexOf(restriction.type) !== -1 || this.affectsMembers(restriction)));
        });
    }
    /**
     * @param restriction
     */
    affectsMembers(restriction) {
        return !isUndefined(restriction.passedMembers) && this.affectsPlayers(restriction) && !this.affectsTeam(restriction);
    }
    /**
     * Gets all the restrictions that affects a player (doesn't equal team member)
     *
     * @param restrictions
     */
    getPlayerRestrictions(restrictions) {
        return filter(restrictions, restriction => {
            return (this.restrictionExceptions.indexOf(restriction.type) === -1 &&
                (this.playerRestrictions.indexOf(restriction.type) !== -1 || this.affectsPlayers(restriction)));
        });
    }
    /**
     * @param restriction
     */
    affectsPlayers(restriction) {
        if (restriction.type === "teamSize") {
            return false;
        }
        return (!restriction.params || !restriction.params.minmembers) && restriction.type !== "logo";
    }
}
RestrictionFilterService.ɵfac = function RestrictionFilterService_Factory(t) { return new (t || RestrictionFilterService)(); };
RestrictionFilterService.ɵprov = i0.ɵɵdefineInjectable({ token: RestrictionFilterService, factory: RestrictionFilterService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RestrictionFilterService, [{
        type: Injectable
    }], null, null); })();

class RestrictionService {
    /**
     * Checks if a team member passes a member restriction
     * A member restriction is a restriction that are not composition based (minmembers) and has passedMembers
     *
     * @param restriction
     * @param memberId
     */
    passesMemberRestriciton(restriction, memberId) {
        return restriction.passedMembers.indexOf(memberId) !== -1;
    }
    /**
     * Checks if a team member passes all member restrictions
     * A member restriction is a restriction that are not composition based (minmembers) and has passedMembers
     *
     * @param restrictions
     * @param memberId
     */
    passesMemberRestrictions(restrictions, memberId) {
        return filter(restrictions, restriction => !this.passesMemberRestriciton(restriction, memberId)).length === 0;
    }
    /**
     * Checks if a team member passes all member restrictions and isn't barred
     * A member restriction is a restriction that are not composition based (minmembers) and has passedMembers
     *
     * @param restrictions
     * @param member
     */
    isMemberEligible(restrictions, member) {
        const passesRestrictions = this.passesMemberRestrictions(restrictions, parseInt(member.user.id, 10));
        return passesRestrictions && !member.user.isBarred;
    }
    /**
     * Returns the failed member entry for the member if exists, else null
     *
     * @param restrictions
     * @param memberId
     */
    getPlayerPPStatus(restrictions, memberId) {
        const restriction = find(restrictions, r => {
            return r.type === "playerPenaltyPoints";
        });
        if (!restriction) {
            return null;
        }
        return find(restriction.failedMembers, (member) => member.id === memberId) || null;
    }
    /**
     * Returns the failed member entry for the member if exists, else null
     *
     * @param restrictions
     * @param memberId
     */
    getPlayerBannedStatus(restrictions, memberId) {
        const restriction = find(restrictions, r => {
            return r.type === "playerLeagueBanned";
        });
        if (!restriction) {
            return null;
        }
        return find(restriction.failedMembers, (member) => member.id === memberId) || null;
    }
    /**
     * Returns the until entry or null if not found
     *
     * @param restrictions
     */
    getTeamPPStatus(restrictions) {
        const restriction = find(restrictions, r => {
            return r.type === "teamPenaltyPoints";
        });
        if (!restriction || restriction.passed) {
            return null;
        }
        return { until: restriction.until };
    }
    /**
     * Returns the failed member entry for the member if exists, else null
     *
     * @param restrictions
     */
    getTeamBannedStatus(restrictions) {
        const restriction = find(restrictions, r => {
            return r.type === "teamLeagueBanned";
        });
        if (!restriction || restriction.passed) {
            return null;
        }
        return { until: restriction.until };
    }
    /**
     * Returns the failed member entry for the member if exists, else null
     *
     * @param restrictions
     * @param memberId
     */
    getAlternateRosterStatus(restrictions, memberId) {
        const restriction = find(restrictions, r => {
            return r.type === "alternateRoster";
        });
        if (!restriction) {
            return null;
        }
        return find(restriction.failedMembers, (member) => member.id === memberId) || null;
    }
}
RestrictionService.ɵfac = function RestrictionService_Factory(t) { return new (t || RestrictionService)(); };
RestrictionService.ɵprov = i0.ɵɵdefineInjectable({ token: RestrictionService, factory: RestrictionService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RestrictionService, [{
        type: Injectable
    }], null, null); })();

class RestrictionTransformerService {
    constructor(filter, playCountryNamesService) {
        this.filter = filter;
        this.playCountryNamesService = playCountryNamesService;
    }
    /**
     * Transforms the teamSize restriction to include a type parameter for translations
     *
     * @param restriction
     */
    transformTeamSizeWithParams(restriction) {
        const minMembers = restriction.params.minmembers;
        const maxMembers = restriction.params.maxmembers;
        let type = "minmax";
        if (minMembers === maxMembers) {
            if (minMembers === 0) {
                type = "any";
            }
            else {
                type = "exact";
            }
        }
        return Object.assign(Object.assign({}, restriction), { params: Object.assign(Object.assign({}, restriction.params), { type: type }) });
    }
    /**
     * Recalculates the passed status depending on passedMembers and the playerId
     * Also filters restrictions out that check teams/rosters
     *
     * @param restrictions
     * @param playerId
     */
    teamRestrictionsToPlayer(restrictions, playerId) {
        const filteredRestrictions = restrictions.filter(restriction => restriction.type !== "logo" && restriction.type !== "ga_team_positions" && restriction.type !== "teamSize");
        return filteredRestrictions.map(restriction => {
            if (!restriction.passedMembers) {
                return restriction;
            }
            return Object.assign(Object.assign({}, restriction), { passed: restriction.passedMembers.indexOf(playerId) !== -1 });
        });
    }
    /**
     * Recalculates the passed status depending on the minmember (or maxmember for teamSize) parameter,
     * the passedMembers and the roster members
     *
     * @param restrictions
     * @param rosterMembers
     */
    teamRestrictionsToRoster(rosterMembers, restrictions) {
        return map(restrictions, restriction => {
            if (restriction.type === "teamSize") {
                const passesMin = restriction.params.minmembers <= rosterMembers.length &&
                    this.doEnoughPlayersPassAllRestrictions(restriction.params.minmembers, restrictions, rosterMembers);
                const passesMax = restriction.params.maxmembers === 0 || restriction.params.maxmembers >= rosterMembers.length;
                return Object.assign(Object.assign({}, restriction), { passed: passesMin && passesMax });
            }
            if (!restriction.params || !restriction.params.minmembers) {
                return restriction;
            }
            const eligiblePlayers = this.getEligibleRosterMembers(restrictions, rosterMembers);
            return this.patchCompositionRestriction(restriction, eligiblePlayers);
        });
    }
    /**
     * Transforms restrictions about countries to include country names
     *
     * @param restriction
     */
    countryCodeToCountryName(restrictions) {
        return restrictions.map((restriction) => {
            let { params } = restriction;
            if (params && params.countries) {
                params = Object.assign(Object.assign({}, params), { countryNames: intlSort(restriction.params.countries.map(country => this.playCountryNamesService.getCountry(country))) });
            }
            if (params && params.nationalities) {
                params = Object.assign(Object.assign({}, params), { nationalityNames: intlSort(restriction.params.nationalities.map(country => this.playCountryNamesService.getCountry(country))) });
            }
            return Object.assign(Object.assign({}, restriction), { params });
        });
    }
    /**
     * We only count players that otherwise pass all member restrictions
     * towards passing the composition restriction.
     *
     * @param restriction A valid composition restriction.
     * @param eligiblePlayers
     */
    patchCompositionRestriction(restriction, eligiblePlayers) {
        const minmembers = restriction.params.minmembers;
        const passingMembers = intersection(eligiblePlayers, restriction.passedMembers);
        const passed = passingMembers.length >= minmembers;
        return Object.assign(Object.assign({}, restriction), { passed: passed });
    }
    /**
     * Get the ids of players passing all player restrictions.
     * (Not composition restrictions!)
     *
     * @param restrictions
     * @param rosterMembers
     */
    getEligibleRosterMembers(restrictions, rosterMembers) {
        // Only care about player restrictions.
        const playerRestrictions = restrictions.filter(restriction => this.filter.affectsMembers(restriction));
        const eligiblePlayers = playerRestrictions.reduce((prev, curr) => {
            return prev.length > 0 ? intersection(prev, curr.passedMembers) : curr.passedMembers;
        }, []);
        return intersection(eligiblePlayers, rosterMembers);
    }
    /**
     * Check if enough players, according to leagues minMember setting pass all
     * member restrictions.
     *
     * @param leagueMinMembers
     * @param restrictions
     * @param rosterMembers
     */
    doEnoughPlayersPassAllRestrictions(leagueMinMembers, restrictions, rosterMembers) {
        const passingPlayers = this.getEligibleRosterMembers(restrictions, rosterMembers);
        return passingPlayers.length >= leagueMinMembers;
    }
}
RestrictionTransformerService.ɵfac = function RestrictionTransformerService_Factory(t) { return new (t || RestrictionTransformerService)(i0.ɵɵinject(RestrictionFilterService), i0.ɵɵinject(PlayCountryNames)); };
RestrictionTransformerService.ɵprov = i0.ɵɵdefineInjectable({ token: RestrictionTransformerService, factory: RestrictionTransformerService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RestrictionTransformerService, [{
        type: Injectable
    }], function () { return [{ type: RestrictionFilterService }, { type: PlayCountryNames }]; }, null); })();

class CommonsRestrictionsModule {
}
CommonsRestrictionsModule.ɵfac = function CommonsRestrictionsModule_Factory(t) { return new (t || CommonsRestrictionsModule)(); };
CommonsRestrictionsModule.ɵmod = i0.ɵɵdefineNgModule({ type: CommonsRestrictionsModule });
CommonsRestrictionsModule.ɵinj = i0.ɵɵdefineInjector({ providers: [RestrictionFilterService, RestrictionService, RestrictionTransformerService], imports: [[CommonsCountryNameModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(CommonsRestrictionsModule, { imports: [CommonsCountryNameModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CommonsRestrictionsModule, [{
        type: NgModule,
        args: [{
                imports: [CommonsCountryNameModule],
                providers: [RestrictionFilterService, RestrictionService, RestrictionTransformerService],
            }]
    }], null, null); })();

class PaginationPipe {
    transform(collection, args) {
        const { page, perPage, active } = args;
        const start = page * perPage;
        const end = start + perPage;
        return active ? collection.slice(start, end) : collection;
    }
}
PaginationPipe.ɵfac = function PaginationPipe_Factory(t) { return new (t || PaginationPipe)(); };
PaginationPipe.ɵpipe = i0.ɵɵdefinePipe({ name: "pagination", type: PaginationPipe, pure: false });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PaginationPipe, [{
        type: Pipe,
        args: [{
                name: "pagination",
                pure: false,
            }]
    }], null, null); })();

class CommonsModule {
}
CommonsModule.ɵfac = function CommonsModule_Factory(t) { return new (t || CommonsModule)(); };
CommonsModule.ɵmod = i0.ɵɵdefineNgModule({ type: CommonsModule });
CommonsModule.ɵinj = i0.ɵɵdefineInjector({ providers: [{ provide: BASE_CONFIG, useValue: commonBaseConfig }, PaginationPipe], imports: [[
            CommonModule,
            CommonsDateModule,
            CommonsLocationModule,
            CommonsErrorModule,
            CommonsLoadingModule,
            CommonsComponentModule,
            CommonsCountryNameModule,
            CommonsRestrictionsModule,
        ]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(CommonsModule, { declarations: [PaginationPipe], imports: [CommonModule,
        CommonsDateModule,
        CommonsLocationModule,
        CommonsErrorModule,
        CommonsLoadingModule,
        CommonsComponentModule,
        CommonsCountryNameModule,
        CommonsRestrictionsModule], exports: [PaginationPipe] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CommonsModule, [{
        type: NgModule,
        args: [{
                declarations: [PaginationPipe],
                imports: [
                    CommonModule,
                    CommonsDateModule,
                    CommonsLocationModule,
                    CommonsErrorModule,
                    CommonsLoadingModule,
                    CommonsComponentModule,
                    CommonsCountryNameModule,
                    CommonsRestrictionsModule,
                ],
                providers: [{ provide: BASE_CONFIG, useValue: commonBaseConfig }, PaginationPipe],
                exports: [PaginationPipe],
            }]
    }], null, null); })();

/*
 * Public API Surface of common
 */

/**
 * Generated bundle index. Do not edit.
 */

export { A, AppBannerComponent, AutofocusDirective, BACKSPACE, BASE_CONFIG, ButtonComponent, CommonsComponentModule, CommonsCountryNameModule, CommonsDateModule, CommonsErrorModule, CommonsFormModule, CommonsLoadingModule, CommonsLocationModule, CommonsModule, CommonsRestrictionsModule, CopyToClipboardDirective, CountryFlagComponent, DELETE, DOWN_ARROW, DescribedCheckboxComponent, END, ENTER, ESCAPE, EmptyCallActionComponent, ExpansionPanelComponent, ExpansionState, FileUploadDropzoneComponent, FileUploadFileComponent, FormatDate, FormatDateComponent, FormatDatePipe, FormatTime, FormatTimePipe, GameIconComponent, HOME, InheritClassComponent, InlineErrorComponent, LEFT_ARROW, LabeledValueComponent, LinkService, LoadingSpinnerComponent, Location, LocationDirective, NINE, PAGE_DOWN, PAGE_UP, PaginationPipe, PlayCountryNames, PlayHeaderComponent, PremiumIconComponent, PrivacyPolicyPopupComponent, ProgressBarComponent, RIGHT_ARROW, RestrictionFilterService, RestrictionService, RestrictionTransformerService, SPACE, SkillIconComponent, SquareImageComponent, StepComponent, StepperComponent, SvgIconComponent, SvgIconsModule, SvgIconsRepository, TAB, TooltipComponent, TooltipDirective, TrustedBadgeComponent, TrustedIconComponent, UP_ARROW, Z, ZERO, commonBaseConfig, countries, demonyms, playRegions };
//# sourceMappingURL=play-common.js.map
